/*==============================================================================
  
  ------------------------------------------------------------------------------

  # DEVELOPMENT INFO

  ### EVENT DELEGATION
  - We utilise the Dom Delegate library in `vendor.js` to better manage event
    delegation.
  - For more info:
    https://github.com/Financial-Times/ftdomdelegate

  ### FUNCTION NAMING
  - Module names are capitalised e.g. `HeaderMain`
  - Functions/methods within modules are camelCase and include an underscore 
    if they are a private method. E.g:
    `_privateFunction` vs. `publicFunction`


  ------------------------------------------------------------------------------

  # TABLE OF CONTENTS

  # UTILITIES
  - __defProp                    public field declaration
  - delegate_default             A global variable to tap into the Dom Delegate library
  - supportsAnimationsAPI        A variable to detect support for Web Animations API
  - Input Binding Manager        Keeps values in sync across various elements
  - Focus Visible Polyfill       Polyfill for focus:visible
  - Trigger Event Helpers        Helpers for triggering events
  - Shopify Event Helpers        Helper to recognise Shopify events in theme editor
  - Throttle Helper              Simple throttle utility
  - Media Features Helper        Helpers to detect media features
  - DOM Helpers                  getSiblings and resolveAsyncIterator
  - Image Helpers                getSizedImageUrl, getMediaSrcset, getSupportedSizes, 
                                 imageLoaded
  - Currency                     formatMoney function
  - Product Loader               Fetch utility to getShopify products
  - Library Loader               Fetch utility to conditionally load scripts
  - QR Code                      QR Code loader for Shopify Gift Card template

  # ELEMENTS
  - Custom HTML Element          A baseline custom web component used as a basis for
                                 multiple custom elements
  - Loading Bar                  Simple loading bar component
  - Openable Element             Used for openable elements such as drawers, popovers, 
                                 modals, etc
  - Loader Button Mixin          Mixin used as prototype for many buttons
  - Loader Button                Used for add to cart and form submissions
  - Toggle Button                Used for toggling open/close various elements
  - Toggle Link                  Link variation of toggle button
  - Confirm Button               Provides confirmation alert to users

  - Get Sticky Header Offset     Utility to determine offset to sticky header
  - Collapsible Content          Collapsibles component
  - Modal Content                Modal component
  - Popup Modal                  Popup Modal for newsletter
  - Combo Box                    Custom select functionality which appears as a
                                 popover on mobile
  - Popover Content              Popover component generally used for mobile
  - Drawer Content               Drawer component used across the site
  - Country Selector             Utility to show provinces on Shopify address forms
  - Quantity Selector            Simple quantity selector component
  - Input Number                 Used in conjunction with quantity selector component
  - Page Pagination              Handles AJAX pagination

  - Announcement Bar             Announcement bar component
  - Header Main                  Main header component
  - Predictive Search Input      Event listener for predictive search input
  - Predictive Search Drawer     Drawer component for predictive search
  - Desktop Navigation           Dropdown and mega menu functionality
  - Mobile Navigation            Drawer for mobile navigation

  - Gesture Area                 Used for various scroll carousels
  - Height Observer              Used for carousels with adaptive heights
  - Safe Sticky                  Sticky component utility
  - Page Dots                    Used for carousel navigation
  - Prev Button                  Used for carousel navigation
  - Next Button                  Used for carousel navigation
  - Scroll Area                  Used for scrollable components
  - Scroll Progress              Used for scrollable components
  - Scroll Shadow                Used for scrollable components

  - Base Carousel                Base carousel
  - Scroll Carousel              Scroll carousel component

  - Product Gallery              Product gallery component
  - Media Carousel               Media carousel component
  - Product Zoom Modal           Product Zoom functionality

  - Product Price                
  - Product Variants             
  - Product Payment Container    
  - Product Form                 

  - Cart Count                   
  - Cart Note                    
  - Line Item                    
  - Line Item Quantity           
  - Cart Notification            
  - Cart Drawer                  

  - Search Page                  

  - Product List                 
  - Hero Image                   

  - Base Media                   
  - Video Media                  
  - Map Element                  
  - Animate on Scroll            
  - Page Transitions             

  # DOM READY
  - InputBindingManager          Instantiate InputBindingManager
  - Window Height                Get window height and store as CSS variable
  - Smooth Scrolling             Smooth scroll for internal links
  - RTE Utilities                Add wrappers to RTE tables and videos
  - Tabbing Focus Utility        Recognise tabbing for accessibility

==============================================================================*/


(() => {

  /*==============================================================================
    ## Utilities
  ==============================================================================*/

  // esbuild

  var __defProp = Object.defineProperty;

  var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;

  var __publicField = (obj, key, value) => {
    __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
    return value;
  };

  var __accessCheck = (obj, member, msg) => {
    if (!member.has(obj))
      throw TypeError("Cannot " + msg);
  };
  var __privateGet = (obj, member, getter) => {
    __accessCheck(obj, member, "read from private field");
    return getter ? getter.call(obj) : member.get(obj);
  };
  var __privateAdd = (obj, member, value) => {
    if (member.has(obj))
      throw TypeError("Cannot add the same private member more than once");
    member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
  };
  var __privateSet = (obj, member, value, setter) => {
    __accessCheck(obj, member, "write to private field");
    setter ? setter.call(obj, value) : member.set(obj, value);
    return value;
  };
  var __privateWrapper = (obj, member, setter, getter) => ({
    set _(value) {
      __privateSet(obj, member, value, setter);
    },
    get _() {
      return __privateGet(obj, member, getter);
    }
  });
  var __privateMethod = (obj, member, method) => {
    __accessCheck(obj, member, "access private method");
    return method;
  };

  /**
   * A simple default variable to hook into the Delegate library
   */
  var delegate_default = Delegate;


  /**
   * Simple way to detect AnimationsAPI. We have some non-important animations
   * that are best achieved in JS. This variable helps ensure the animations
   * don't run for older browsers.
   */
  var supportsAnimationsAPI = 'animate' in document.body;









  /**
   * =============================================================
   * ## INPUT BINDING MANAGER
   *    Used to keep values in sync across various elements. For example
   *    if using a product sticky form, we want both the main form and
   *    sticky form to be in sync dependning on user choice.
   * =============================================================
   */

  var InputBindingManager = class {
    constructor() {
      this.delegateElement = new delegate_default(document.body);
      this.delegateElement.on("change", "[data-bind-value]", this._onValueChanged.bind(this));
    }

    _onValueChanged(event, target) {
      const boundElement = document.getElementById(target.getAttribute("data-bind-value"));
      if (boundElement) {
        if (target.tagName === "SELECT") {
          target = target.options[target.selectedIndex];
        }
        boundElement.innerHTML = target.hasAttribute("title") ? target.getAttribute("title") : target.value;
      }
    }
  };








  /**
   * =============================================================
   * ## Focus Visible Polyfill
   * =============================================================
   */

  // Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
  try {
    document.querySelector(":focus-visible");
  } catch(e) {
    focusVisiblePolyfill();
  }

  function focusVisiblePolyfill() {
    const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
    let currentFocusedElement = null;
    let mouseClick = null;

    window.addEventListener('keydown', (event) => {
      if(navKeys.includes(event.code.toUpperCase())) {
        mouseClick = false;
      }
    });

    window.addEventListener('mousedown', (event) => {
      mouseClick = true;
    });

    window.addEventListener('focus', () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focus-visible');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focus-visible');

    }, true);
  }










  /**
   * =============================================================
   * ## Trigger Event Helper
   * =============================================================
   */

  function triggerEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: data
    }));
  }

  function triggerNonBubblingEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: false,
      detail: data
    }));
  }










  /**
   * =============================================================
   * ## filterShopifyEvent
   * A helper to recognise Shopify events in the Theme Editor
   * =============================================================
   */
  function filterShopifyEvent(event, domElement, callback) {
    let executeCallback = false;
    if (event.type.includes("shopify:section")) {
      if (domElement.hasAttribute("section") && domElement.getAttribute("section") === event.detail.sectionId) {
        executeCallback = true;
      }
    } else if (event.type.includes("shopify:block") && event.target === domElement) {
      executeCallback = true;
    }
    if (executeCallback) {
      callback(event);
    }
  }









  /**
   * =============================================================
   * ## Throttle Helper
   * =============================================================
   */

  function throttle(callback, delay3 = 15) {
    let throttleTimeout = null, storedEvent = null;

    const throttledEventHandler = (event) => {
      storedEvent = event;
      const shouldHandleEvent = !throttleTimeout;
      if (shouldHandleEvent) {
        callback(storedEvent);
        storedEvent = null;
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
          if (storedEvent) {
            throttledEventHandler(storedEvent);
          }
        }, delay3);
      }
    };
    return throttledEventHandler;
  }

  function debounce(fn, delay3) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn.apply(this, args);
      }, delay3);
    };
  }










  /**
   * =============================================================
   * ## Media Features Helper
   * =============================================================
   */

  var MediaFeatures = class {
    static prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    static supportsHover() {
      return window.matchMedia("(pointer: fine)").matches;
    }
  };










  /**
   * =============================================================
   * ## DOM helpers
   * 
   * 1. getSiblings
   * 2. resolveAsyncIterator
   * =============================================================
   */

  function getSiblings(element, filter, includeSelf = false) {
    let siblings = [];
    let currentElement = element;
    while (currentElement = currentElement.previousElementSibling) {
      if (!filter || currentElement.matches(filter)) {
        siblings.push(currentElement);
      }
    }
    if (includeSelf) {
      siblings.push(element);
    }
    currentElement = element;
    while (currentElement = currentElement.nextElementSibling) {
      if (!filter || currentElement.matches(filter)) {
        siblings.push(currentElement);
      }
    }
    return siblings;
  }

  async function resolveAsyncIterator(target) {
    const processedTarget = [];
    if (!(target != null && typeof target[Symbol.iterator] === "function")) {
      target = [target];
    }
    for (const targetItem of target) {
      if (typeof targetItem[Symbol.asyncIterator] === "function") {
        for await (const awaitTarget of targetItem) {
          processedTarget.push(awaitTarget);
        }
      } else {
        processedTarget.push(targetItem);
      }
    }
    return processedTarget;
  }










  /**
   * =============================================================
   * ## IMAGE HELPERS
   * =============================================================
   */

  function getSizedMediaUrl(media, size) {
    let src = typeof media === "string" ? media : media["preview_image"] ? media["preview_image"]["src"] : media["url"];
    if (size === null) {
      return src;
    }
    if (size === "master") {
      return src.replace(/http(s)?:/, "");
    }
    const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i);
    if (match) {
      const prefix = src.split(match[0]);
      const suffix = match[0];
      return (prefix[0] + "_" + size + suffix).replace(/http(s)?:/, "");
    } else {
      return null;
    }
  }

  function getMediaSrcset(media, sizeList) {
    let srcset = [], supportedSizes = typeof media === "string" ? sizeList : getSupportedSizes(media, sizeList);
    supportedSizes.forEach((supportedSize) => {
      srcset.push(`${getSizedMediaUrl(media, supportedSize + "x")} ${supportedSize}w`);
    });
    return srcset.join(",");
  }

  function getSupportedSizes(media, desiredSizes) {
    let supportedSizes = [], mediaWidth = media["preview_image"]["width"];
    desiredSizes.forEach((width) => {
      if (mediaWidth >= width) {
        supportedSizes.push(width);
      }
    });
    return supportedSizes;
  }

  function imageLoaded(image) {
    return new Promise((resolve) => {
      if (!image || image.tagName !== "IMG" || image.complete) {
        resolve();
      } else {
        image.onload = () => resolve();
      }
    });
  }










  /**
   * =============================================================
   * ## CURRENCY
   * =============================================================
   */
  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    let value = "";
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }
    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }










  /**
   * =============================================================
   * ## PRODUCT LOADER
   * =============================================================
   */
  var ProductLoader = class {
    static load(productHandle) {
      if (!productHandle) {
        return;
      }

      if (this.loadedProducts[productHandle]) {
        return this.loadedProducts[productHandle];
      }

      this.loadedProducts[productHandle] = new Promise(async (resolve) => {
        const response = await fetch(`${window.themeVariables.routes.rootUrlWithoutSlash}/products/${productHandle}.js`);
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      });

      return this.loadedProducts[productHandle];
    }
  };

  __publicField(ProductLoader, "loadedProducts", {});











  /**
   * =============================================================
   * ## LIBRARY LOADER
   * =============================================================
   */
  var LibraryLoader = class {
    static load(libraryName) {
      const STATUS_REQUESTED = "requested";
      const STATUS_LOADED = "loaded";
      const library = this.libraries[libraryName];

      if (!library) {
        return;
      }

      if (library.status === STATUS_REQUESTED) {
        return library.promise;
      }

      if (library.status === STATUS_LOADED) {
        return Promise.resolve();
      }

      let promise;
      if (library.type === "script") {
        promise = new Promise((resolve, reject) => {
          let tag = document.createElement("script");
          tag.id = library.tagId;
          tag.src = library.src;
          tag.onerror = reject;
          tag.onload = () => {
            library.status = STATUS_LOADED;
            resolve();
          };
          document.body.appendChild(tag);
        });
      } else {
        promise = new Promise((resolve, reject) => {
          let tag = document.createElement("link");
          tag.id = library.tagId;
          tag.href = library.src;
          tag.rel = "stylesheet";
          tag.type = "text/css";
          tag.onerror = reject;
          tag.onload = () => {
            library.status = STATUS_LOADED;
            resolve();
          };
          document.body.appendChild(tag);
        });
      }

      library.promise = promise;
      library.status = STATUS_REQUESTED;
      return promise;
    }
  };

  __publicField(LibraryLoader, "libraries", {
    qrCode: {
      tagId: "qrCode",
      src: window.themeVariables.libs.qrCode,
      type: "script"
    },
    modelViewerUiStyles: {
      tagId: "shopify-model-viewer-ui-styles",
      src: "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css",
      type: "link"
    }
  });








  /**
   * =============================================================
   * ## QR CODE
   * =============================================================
   */
  var QrCode = class extends HTMLElement {
    async connectedCallback() {
      await LibraryLoader.load("qrCode");
      new window.QRCode(this, {
        text: this.getAttribute("identifier"),
        width: 200,
        height: 200
      });
    }
  };

  window.customElements.define("qr-code", QrCode);











  /**
   * =============================================================
   * ## CustomHTMLElement
   * 
   * A custom implementation of HTMLElements which includes
   * 1. Variable to indicate a section has been reloaded in Shopify theme editor
   * 2. Storing rootDelegate and delegate of elements
   * 3. Trigger events to show and hide a loading bar
   * 4. A unique disconnectedCallback to disconnect the previous functions
   * =============================================================
   */

  var CustomHTMLElement = class extends HTMLElement {
    constructor() {
      super();

      this._hasSectionReloaded = false;

      if (Shopify.designMode) {
        this.rootDelegate.on("shopify:section:select", (event) => {
          const parentSection = this.closest(".shopify-section");
          if (event.target === parentSection && event.detail.load) {
            this._hasSectionReloaded = true;
          }
        });
      }
    }

    get rootDelegate() {
      return this._rootDelegate = this._rootDelegate || new delegate_default(document.documentElement);
    }

    get delegate() {
      return this._delegate = this._delegate || new delegate_default(this);
    }

    showLoadingBar() {
      triggerEvent(document.documentElement, "theme:loading:start");
    }

    hideLoadingBar() {
      triggerEvent(document.documentElement, "theme:loading:end");
    }

    disconnectedCallback() {
      this.delegate.destroy();
      this.rootDelegate.destroy();
      delete this._delegate;
      delete this._rootDelegate;
    }
  };










  /**
   * =============================================================
   * ## LoadingBar
   * =============================================================
   */

  var LoadingBar = class extends CustomHTMLElement {
    constructor() {
      super();
      this.rootDelegate.on("theme:loading:start", this.show.bind(this));
      this.rootDelegate.on("theme:loading:end", this.hide.bind(this));
      this.delegate.on("transitionend", this._onTransitionEnd.bind(this));
    }

    show() {
      this.classList.add("is-visible");
      this.style.transform = "scaleX(0.4)";
    }
    
    hide() {
      this.style.transform = "scaleX(1)";
      this.classList.add("is-finished");
    }

    _onTransitionEnd(event) {
      if (event.propertyName === "transform" && this.classList.contains("is-finished")) {
        this.classList.remove("is-visible");
        this.classList.remove("is-finished");
        this.style.transform = "scaleX(0)";
      }
    }
  };

  window.customElements.define("loading-bar", LoadingBar);











  /**
   * =============================================================
   * ## OPENABLE ELEMENT
   * =============================================================
   */

  var OpenableElement = class extends CustomHTMLElement {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();

      // Open or close based on theme editor events
      if (Shopify.designMode) {
        this.rootDelegate.on("shopify:section:select", (event) => filterShopifyEvent(event, this, () => this.open = true));
        this.rootDelegate.on("shopify:section:deselect", (event) => filterShopifyEvent(event, this, () => this.open = false));
      }

      // if append-body, clone to body, and remove from flow
      if (this.hasAttribute("append-body")) {
        const existingNode = document.getElementById(this.id);
        this.removeAttribute("append-body");

        if (existingNode && existingNode !== this) {
          existingNode.replaceWith(this.cloneNode(true));
          this.remove();
        } else {
          document.body.appendChild(this);
        }
      }
    }

    connectedCallback() {
      // Event handlers to close
      this.delegate.on("click", ".openable__overlay", () => this.open = false);
      this.delegate.on("click", '[data-action="close"]', (event) => {
        // ensure close event does not bubble up
        event.stopPropagation();
        this.open = false;
      });
    }

    get requiresLoading() {
      return this.hasAttribute("href");
    }

    get open() {
      return this.hasAttribute("open");
    }

    set open(value) {
      if (value) {
        (async () => {
          await this._load();
          this.clientWidth;
          this.setAttribute("open", "");
        })();
      } else {
        this.removeAttribute("open");
      }
    }

    get shouldTrapFocus() {
      return true;
    }

    get returnFocusOnDeactivate() {
      return !this.hasAttribute("return-focus") || this.getAttribute("return-focus") === "true";
    }

    get focusTrap() {
      return this._focusTrap = this._focusTrap || focusTrap.createFocusTrap(this, {
        fallbackFocus: this,
        initialFocus: this.hasAttribute("initial-focus-selector") ? this.getAttribute("initial-focus-selector") : void 0,
        clickOutsideDeactivates: (event) => !(event.target.hasAttribute("aria-controls") && event.target.getAttribute("aria-controls") === this.id),
        allowOutsideClick: (event) => event.target.hasAttribute("aria-controls") && event.target.getAttribute("aria-controls") === this.id,
        returnFocusOnDeactivate: this.returnFocusOnDeactivate,
        onDeactivate: () => this.open = false,
        preventScroll: true
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "open":
          if (oldValue === null && newValue === "") {
            if (this.shouldTrapFocus) {
              setTimeout(() => this.focusTrap.activate(), 150);
            }
            triggerEvent(this, "openable-element:open");
          } else if (newValue === null) {
            if (this.shouldTrapFocus) {
              this.focusTrap.deactivate();
            }
            triggerEvent(this, "openable-element:close");
          }
      }
    }

    async _load() {
      if (!this.requiresLoading) {
        return;
      }

      triggerNonBubblingEvent(this, "openable-element:load:start");

      const response = await fetch(this.getAttribute("href"));

      const element = document.createElement("div");
      element.innerHTML = await response.text();

      this.innerHTML = element.querySelector(this.tagName.toLowerCase()).innerHTML;

      this.removeAttribute("href");

      triggerNonBubblingEvent(this, "openable-element:load:end");
    }
  };

  window.customElements.define("openable-element", OpenableElement);










  /**
   * =============================================================
   * ## LOADER BUTTON MIXIN
   * =============================================================
   */

  var LoaderButtonMixin = {
    _prepareButton() {
      this.originalContent = this.innerHTML;
      this._startTransitionPromise = null;

      this.innerHTML = `
      <span class="loader-button__text">${this.innerHTML}</span>
      <span class="loader-button__loader" hidden>
        <div class="spinner">
          <svg focusable="false" width="24" height="24" class="icon icon--spinner" viewBox="25 25 50 50">
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
          </svg>
        </div>
      </span>
    `;
      this.textElement = this.firstElementChild;
      this.spinnerElement = this.lastElementChild;
      window.addEventListener("pagehide", () => this.removeAttribute("aria-busy"));
    },

    _startTransition() {
      const textAnimation = this.textElement.animate({
        opacity: [1, 0],
        transform: ["translateY(0)", "translateY(-10px)"]
      }, {
        duration: 75,
        easing: "ease",
        fill: "forwards"
      });

      this.spinnerElement.hidden = false;

      const spinnerAnimation = this.spinnerElement.animate({
        opacity: [0, 1],
        transform: ["translate(-50%, 0%)", "translate(-50%, -50%)"]
      }, {
        duration: 75,
        delay: 75,
        easing: "ease",
        fill: "forwards"
      });

      this._startTransitionPromise = Promise.all([

        new Promise((resolve) => textAnimation.onfinish = () => resolve()),
        new Promise((resolve) => spinnerAnimation.onfinish = () => resolve())
      ]);
    },

    async _endTransition() {
      if (!this._startTransitionPromise) {
        return;
      }

      await this._startTransitionPromise;
      this.spinnerElement.animate({
        opacity: [1, 0],
        transform: ["translate(-50%, -50%)", "translate(-50%, -100%)"]
      }, {
        duration: 75,
        delay: 100,
        easing: "ease",
        fill: "forwards"
      }).onfinish = () => this.spinnerElement.hidden = true;
      this.textElement.animate({
        opacity: [0, 1],
        transform: ["translateY(10px)", "translateY(0)"]
      }, {
        duration: 75,
        delay: 175,
        easing: "ease",
        fill: "forwards"
      });
      this._startTransitionPromise = null;
    }
  };









  /**
   * =============================================================
   * ## LOADER BUTTON
   * =============================================================
   */
  var LoaderButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-busy"];
    }

    constructor() {
      super();

      this.addEventListener("click", (event) => {
        if (this.type === "submit" && this.form && this.form.checkValidity() && !this.form.hasAttribute("is")) {
          if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
            event.preventDefault();

            this.setAttribute("aria-busy", "true");
            setTimeout(() => this.form.submit(), 250);
          } else {
            this.setAttribute("aria-busy", "true");
          }
        }
      });
    }

    connectedCallback() {
      this._prepareButton();
    }

    disconnectedCallback() {
      this.innerHTML = this.originalContent;
    }

    attributeChangedCallback(property, oldValue, newValue) {
      if (property === "aria-busy" && supportsAnimationsAPI) {
        if (newValue === "true") {
          this._startTransition();
        } else {
          this._endTransition();
        }
      }
    }
  };

  Object.assign(LoaderButton.prototype, LoaderButtonMixin);

  window.customElements.define("loader-button", LoaderButton, { extends: "button" });











  /**
   * =============================================================
   * ## TOGGLE BUTTON
   * =============================================================
   */

  var ToggleButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-expanded", "aria-busy"];
    }

    constructor() {
      super();

      if (this.hasAttribute("loader")) {
        this._prepareButton();
      }

      this.addEventListener("click", this._onButtonClick.bind(this));
      this.rootDelegate = new delegate_default(document.documentElement);
    }

    _onButtonClick(event) {
      // Collapsible specific toggle which closes other collapsibles
      if (this.classList.contains('collapsible-toggle--one-only')) {
        this.parentElement.querySelectorAll(".collapsible-toggle--one-only").forEach((item) => {
            if (item.isExpanded && item !== this) {
              item.isExpanded = false;
            }
        });
      }

      this.isExpanded = !this.isExpanded;
    }

    connectedCallback() {
      document.addEventListener("openable-element:close", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
          event.stopPropagation();
        }
      });

      document.addEventListener("openable-element:open", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
          event.stopPropagation();
        }
      });

      this.rootDelegate.on("openable-element:load:start", `#${this.getAttribute("aria-controls")}`, () => {
        if (this.classList.contains("button")) {
          this.setAttribute("aria-busy", "true");
        } else if (this.offsetParent !== null) {
          triggerEvent(document.documentElement, "theme:loading:start");
        }
      }, true);

      this.rootDelegate.on("openable-element:load:end", `#${this.getAttribute("aria-controls")}`, () => {
        if (this.classList.contains("button")) {
          this.removeAttribute("aria-busy");
        } else if (this.offsetParent !== null) {
          triggerEvent(document.documentElement, "theme:loading:end");
        }
      }, true);
    }

    disconnectedCallback() {
      this.rootDelegate.destroy();
    }

    get isExpanded() {
      return this.getAttribute("aria-expanded") === "true";
    }

    set isExpanded(value) {
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get controlledElement() {
      return document.getElementById(this.getAttribute("aria-controls"));
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "aria-expanded":
          if (oldValue === "false" && newValue === "true") {
            this.controlledElement.open = true;
          } else if (oldValue === "true" && newValue === "false") {
            this.controlledElement.open = false;
          }
          break;
        case "aria-busy":
          if (this.hasAttribute("loader")) {
            if (newValue === "true") {
              this._startTransition();
            } else {
              this._endTransition();
            }
          }
          break;
      }
    }
  };

  Object.assign(ToggleButton.prototype, LoaderButtonMixin);

  window.customElements.define("toggle-button", ToggleButton, { extends: "button" });




  var ProductCollapsiblesToggle = class extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-action="open-all"]').addEventListener("click", this._openAll.bind(this));
      this.querySelector('[data-action="close-all"]').addEventListener("click", this._closeAll.bind(this));
      this.productCollapsibles = document.querySelector(".product-info__collapsibles-items");
    }

    _openAll(event) {
      this.productCollapsibles.querySelectorAll(".collapsible").forEach((item) => {
        if (item.hasAttribute("open")) {
          return;
        }

        item.setAttribute("open", "");
        let id = item.id;
        this.productCollapsibles.querySelector("#" + id).setAttribute("aria-expanded", "true");
      });
    }

    _closeAll(event) {
      this.productCollapsibles.querySelectorAll(".collapsible").forEach((item) => {
        if (item.hasAttribute("open")) {
          item.removeAttribute("open");
          let id = item.id;
          this.productCollapsibles.querySelector("#" + id).setAttribute("aria-expanded", "false");
        }
      });
    }
  }

  window.customElements.define("product-collapsibles-toggle", ProductCollapsiblesToggle);








  /**
   * =============================================================
   * ## TOGGLE LINK
   * =============================================================
   */

  var ToggleLink = class extends HTMLAnchorElement {
    static get observedAttributes() {
      return ["aria-expanded"];
    }

    constructor() {
      super();
      this.addEventListener("click", (event) => {
        event.preventDefault();
        this.isExpanded = !this.isExpanded;
      });

      this.rootDelegate = new delegate_default(document.documentElement);
    }

    connectedCallback() {
      this.rootDelegate.on("openable-element:close", `#${this.getAttribute("aria-controls")}`, (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
        }
      }, true);

      this.rootDelegate.on("openable-element:open", `#${this.getAttribute("aria-controls")}`, (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
        }
      }, true);
    }

    disconnectedCallback() {
      this.rootDelegate.destroy();
    }

    get isExpanded() {
      return this.getAttribute("aria-expanded") === "true";
    }

    set isExpanded(value) {
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get controlledElement() {
      return document.querySelector(`#${this.getAttribute("aria-controls")}`);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "aria-expanded":
          if (oldValue === "false" && newValue === "true") {
            this.controlledElement.open = true;
          } else if (oldValue === "true" && newValue === "false") {
            this.controlledElement.open = false;
          }
      }
    }
  };

  window.customElements.define("toggle-link", ToggleLink, { extends: "a" });










  /**
   * =============================================================
   * ## CONFIRM BUTTON
   * =============================================================
   */

  var ConfirmButton = class extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener("click", (event) => {
        if (!window.confirm(this.getAttribute("data-message") || "Are you sure you wish to do this?")) {
          event.preventDefault();
        }
      });
    }
  };

  window.customElements.define("confirm-button", ConfirmButton, { extends: "button" });











  /**
   * =============================================================
   * ## GET STICKY HEADER OFFSET
   * =============================================================
   */

  function getStickyHeaderOffset() {
    const documentStyles = getComputedStyle(document.documentElement);
    return parseInt(documentStyles.getPropertyValue("--header-height") || 0) * parseInt(documentStyles.getPropertyValue("--enable-sticky-header") || 0) + parseInt(documentStyles.getPropertyValue("--announcement-bar-height") || 0) * parseInt(documentStyles.getPropertyValue("--enable-sticky-announcement-bar") || 0);
  }










  /**
   * =============================================================
   * ## COLLAPSIBLE CONTENT
   * =============================================================
   */
  var CollapsibleContent = class extends OpenableElement {
    constructor() {
      super();
      this.ignoreNextTransition = this.open;
      this.addEventListener("shopify:block:select", () => this.open = true);
      this.addEventListener("shopify:block:deselect", () => this.open = false);
    }

    get animateItems() {
      return this.hasAttribute("animate-items");
    }

    get maxHeight() {
      return this.hasAttribute("max-height"); 
    }

    attributeChangedCallback(name) {
      if (this.ignoreNextTransition) {
        return this.ignoreNextTransition = false;
      }

      switch (name) {
        case "open":
          if (!supportsAnimationsAPI) {
            triggerEvent(this, this.open ? "openable-element:open" : "openable-element:close");
            return;
          }

          let scrollHeight = `${this.scrollHeight}px`;

          if (!this.maxHeight) {
            this.style.overflowY = "hidden";
          } else {
            scrollHeight = this.scrollHeight < 241 ? `${this.scrollHeight}px` : "240px";
          }

          const keyframes = {
            height: ["0px", `${this.scrollHeight}px`],
            visibility: ["hidden", "visible"]
          };

          if (this.animateItems) {
            keyframes["opacity"] = this.open ? [0, 0] : [0, 1];
          }

          this.animate(keyframes, {
            duration: 500,
            direction: this.open ? "normal" : "reverse",
            easing: "cubic-bezier(0.75, 0, 0.175, 1)"
          }).onfinish = () => {
            if (!this.maxHeight) {
              this.style.overflow = this.open ? "visible" : "hidden";
            }
          };

          if (this.animateItems && this.open) {
            this.animate({
              opacity: [0, 1],
              transform: ["translateY(10px)", "translateY(0)"]
            }, {
              duration: 250,
              delay: 250,
              easing: "cubic-bezier(0.75, 0, 0.175, 1)"
            });
          }

          triggerEvent(this, this.open ? "openable-element:open" : "openable-element:close");
      }
    }
  };

  window.customElements.define("collapsible-content", CollapsibleContent);









  /**
   * =============================================================
   * ## MODAL CONTENT
   * =============================================================
   */
  var ModalContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.delegate.on("click", ".modal__overlay", () => this.open = false);
    }

    get lockBody() {
      return this.hasAttribute("lock-body");
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          if (this.lockBody) {
            document.documentElement.classList.toggle("lock-all", this.open);
          }
      }
    }
  };

  window.customElements.define("modal-content", ModalContent);










  /**
   * =============================================================
   * ## POPUP
   * =============================================================
   */
  var PopupModal = class extends ModalContent {
    connectedCallback() {
      super.connectedCallback();

      if (this.appearAfterDelay && !(this.onlyOnce && this.hasAppearedOnce)) {
        setTimeout(() => this.open = true, this.delay);
      }
    }

    get shouldTrapFocus() {
      return false;
    }

    get appearAfterDelay() {
      return this.hasAttribute("delay");
    }

    get delay() {
      return parseInt(this.getAttribute("delay") || 0) * 1e3;
    }

    get onlyOnce() {
      return this.hasAttribute("only-once");
    }

    get hasAppearedOnce() {
      return localStorage.getItem("theme:popup-appeared") !== null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      switch (name) {
        case "open":
          if (this.open) {
            localStorage.setItem("theme:popup-appeared", true);
          }
      }
    }
  };

  window.customElements.define("popup-modal", PopupModal);









  /**
   * =============================================================
   * ## COMBO BOX
   * - Custom Select functionality which is styled like a popover on mobile
   * =============================================================
   */

  var ComboBox = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.options = Array.from(this.querySelectorAll('[role="option"]'));

      this.delegate.on("click", '[role="option"]', this._onValueClicked.bind(this));
      this.delegate.on("keydown", '[role="listbox"]', this._onKeyDown.bind(this));
      this.delegate.on("change", "select", this._onValueChanged.bind(this));
      this.delegate.on("click", ".combo-box__overlay", () => this.open = false);

      // fit-toggle attribute used for sticky form option if we want in future
      if (this.hasAttribute("fit-toggle")) {
        const maxWidth = Math.max(...this.options.map((item) => item.clientWidth)), control = document.querySelector(`[aria-controls="${this.id}"]`);
        if (control) {
          control.style.setProperty("--largest-option-width", `${maxWidth + 2}px`);
        }
      }
    }

    get nativeSelect() {
      return this.querySelector("select");
    }

    set selectedValue(value) {
      this.options.forEach((option) => {
        option.setAttribute("aria-selected", option.getAttribute("value") === value ? "true" : "false");
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      switch (name) {
        case "open":
          if (this.open) {
            const boundingRect = this.getBoundingClientRect();
            this.classList.toggle("combo-box--top", boundingRect.top >= window.innerHeight / 2 * 1.5);
            setTimeout(() => this.focusTrap.activate(), 150);
          } else {
            this.focusTrap.deactivate();
            setTimeout(() => this.classList.remove("combo-box--top"), 200);
          }

          document.documentElement.classList.toggle("lock-mobile", this.open);
      }
    }

    _onValueClicked(event, target) {
      this.selectedValue = target.value;
      this.nativeSelect.value = target.value;
      this.nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      
      // Find the visual swatch in the clicked option
      const optionSwatch = target.querySelector('.visual-swatch');
      
      if (optionSwatch) {
        // Find the toggle button that controls this combo-box
        const toggleButton = document.querySelector(`[aria-controls="${this.id}"]`);
        
        if (toggleButton) {
          // Find or create the swatch container in the toggle button
          let swatchContainer = toggleButton.querySelector('.visual-swatch-container');
          
          if (!swatchContainer) {
            swatchContainer = document.createElement('span');
            swatchContainer.className = 'visual-swatch-container';
            const selectedValueSpan = toggleButton.querySelector('.select__selected-value');
            if (selectedValueSpan) {
              selectedValueSpan.after(swatchContainer);
            }
          }
          
          // Clone the swatch from the option to the toggle button
          swatchContainer.innerHTML = '';
          swatchContainer.appendChild(optionSwatch.cloneNode(true));
        }
      }
      
      this.open = false;
    }

    _onValueChanged(event, target) {
      Array.from(this.nativeSelect.options).forEach((option) => option.toggleAttribute("selected", target.value === option.value));
      this.selectedValue = target.value;
    }

    _onKeyDown(event) {
      var _a, _b;

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();

        if (event.key === "ArrowDown") {
          (_a = document.activeElement.nextElementSibling) == null ? void 0 : _a.focus();
        } else {
          (_b = document.activeElement.previousElementSibling) == null ? void 0 : _b.focus();
        }
      }
    }
  };

  window.customElements.define("combo-box", ComboBox);











  /**
   * =============================================================
   * ## POPOVER CONTENT
   * =============================================================
   */
  var PopoverContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.delegate.on("click", ".popover__overlay", () => this.open = false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-mobile", this.open);
      }
    }
  };
  
  window.customElements.define("popover-content", PopoverContent);











  /**
   * =============================================================
   * ## DRAWER CONTENT
   * =============================================================
   */

  var DrawerContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      if (this.hasAttribute("reverse-breakpoint")) {
        this.originalDirection = this.classList.contains("drawer--from-left") ? "left" : "right";

        const matchMedia2 = window.matchMedia(this.getAttribute("reverse-breakpoint"));
        matchMedia2.addListener(this._checkReverseOpeningDirection.bind(this));

        this._checkReverseOpeningDirection(matchMedia2);
      }
      this.delegate.on("click", ".drawer__overlay", () => this.open = false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-all", this.open);
      }
    }

    _checkReverseOpeningDirection(match) {
      this.classList.remove("drawer--from-left");
      if (this.originalDirection === "left" && !match.matches || this.originalDirection !== "left" && match.matches) {
        this.classList.add("drawer--from-left");
      }
    }
  };

  window.customElements.define("drawer-content", DrawerContent);










  /**
   * =============================================================
   * ## COUNTRY SELECTOR
   * =============================================================
   */
  var CountrySelector = class extends HTMLSelectElement {
    connectedCallback() {
      this.provinceElement = document.getElementById(this.getAttribute("aria-owns"));
      this.addEventListener("change", this._updateProvinceVisibility.bind(this));

      if (this.hasAttribute("data-default")) {
        for (let i = 0; i !== this.options.length; ++i) {
          if (this.options[i].text === this.getAttribute("data-default")) {
            this.selectedIndex = i;
            break;
          }
        }
      }

      if (this.provinceElement.hasAttribute("data-default")) {
        for (let i = 0; i !== this.provinceElement.options.length; ++i) {
          if (this.provinceElement.options[i].text === this.provinceElement.getAttribute("data-default")) {
            this.provinceElement.selectedIndex = i;
            break;
          }
        }
      }
      this._updateProvinceVisibility();
    }

    _updateProvinceVisibility() {
      const selectedOption = this.options[this.selectedIndex];

      if (!selectedOption) {
        return;
      }

      let provinces = JSON.parse(selectedOption.getAttribute("data-provinces") || "[]");
      let provinceSelectElement = this.provinceElement.tagName === "SELECT" ? this.provinceElement : this.provinceElement.querySelector("select");

      provinceSelectElement.innerHTML = "";

      if (provinces.length === 0) {
        this.provinceElement.hidden = true;
        return;
      }

      provinces.forEach((data) => {
        provinceSelectElement.options.add(new Option(data[1], data[0]));
      });

      this.provinceElement.hidden = false;
    }
  };

  window.customElements.define("country-selector", CountrySelector, { extends: "select" });









  /**
   * =============================================================
   * ## QUANTITY SELECTOR
   * =============================================================
   */
  var QuantitySelector = class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this.inputElement = this.querySelector("input");

      this.querySelector("button:first-of-type").addEventListener("click", () => this.inputElement.quantity = this.inputElement.quantity - 1, { signal: this._abortController.signal });
      this.querySelector("button:last-of-type").addEventListener("click", () => this.inputElement.quantity = this.inputElement.quantity + 1, { signal: this._abortController.signal });
    }

    disconnectedCallback() {
      this._abortController.abort();
    }
  };

  if (!window.customElements.get("quantity-selector")) {
    window.customElements.define("quantity-selector", QuantitySelector);
  };

  var QuantityInput = class extends HTMLInputElement {
    constructor() {
      super();
      this.addEventListener("input", this._onValueInput);
      this.addEventListener("change", this._onValueChanged);
      this.addEventListener("keydown", this._onKeyDown);
      this.addEventListener("focus", this.select);
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    get quantity() {
      return parseInt(this.value);
    }

    set quantity(quantity) {
      const isNumeric = (typeof quantity === "number" || typeof quantity === "string" && quantity.trim() !== "") && !isNaN(quantity);

      if (quantity === "") {
        return;
      }

      if (!isNumeric || quantity < 0) {
        quantity = parseInt(quantity) || 1;
      }

      this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
      this.size = Math.max(this.value.length + 1, 2);
    }

    _onValueInput() {
      this.quantity = this.value;
    }

    _onValueChanged() {
      if (this.value === "") {
        this.quantity = 1;
      }
    }

    _onKeyDown(event) {
      event.stopPropagation();
      const originalQuantity = this.quantity;

      if (event.key === "ArrowUp") {
        this.quantity = this.quantity + 1;
      } else if (event.key === "ArrowDown") {
        this.quantity = this.quantity - 1;
      }

      if (originalQuantity !== this.quantity) {
        this._abortController = new AbortController();
        this.addEventListener("blur", () => this.dispatchEvent(new Event("change", { bubbles: true })), { once: true, signal: this._abortController.signal });
      }
    }
  };

  if (!window.customElements.get("quantity-input")) {
    window.customElements.define("quantity-input", QuantityInput, { extends: "input" });
  };







  /**
   * =============================================================
   * ## INPUT NUMBER
   * =============================================================
   */
  var InputNumber = class extends HTMLInputElement {
    connectedCallback() {
      this.addEventListener("input", this._onValueInput.bind(this));
      this.addEventListener("change", this._onValueChanged.bind(this));
      this.addEventListener("keydown", this._onKeyDown.bind(this));
    }

    get quantity() {
      return parseInt(this.value);
    }

    set quantity(quantity) {
      const isNumeric = (typeof quantity === "number" || typeof quantity === "string" && quantity.trim() !== "") && !isNaN(quantity);

      if (quantity === "") {
        return;
      }

      if (!isNumeric || quantity < 0) {
        quantity = parseInt(quantity) || 1;
      }

      this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
      this.size = Math.max(this.value.length + 1, 2);
    }

    _onValueInput() {
      this.quantity = this.value;
    }

    _onValueChanged() {
      if (this.value === "") {
        this.quantity = 1;
      }
    }

    _onKeyDown(event) {
      event.stopPropagation();

      if (event.key === "ArrowUp") {
        this.quantity = this.quantity + 1;
      } else if (event.key === "ArrowDown") {
        this.quantity = this.quantity - 1;
      }
    }
  };

  window.customElements.define("input-number", InputNumber, { extends: "input" });









  /**
   * =============================================================
   * ## PAGE PAGINATION
   * =============================================================
   */

  var PagePagination = class extends CustomHTMLElement {
    connectedCallback() {
      if (this.hasAttribute("ajax")) {
        this.delegate.on("click", "a", this._onLinkClicked.bind(this));
      }
    }
    _onLinkClicked(event, target) {
      event.preventDefault();

      const url = new URL(window.location.href);
      url.searchParams.set("page", target.getAttribute("data-page"));
      triggerEvent(this, "pagination:page-changed", { url: url.toString() });
    }
  };

  window.customElements.define("page-pagination", PagePagination);












var _reveal, reveal_fn;
var RevealItems = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _reveal);
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      Motion.inView(this, __privateMethod(this, _reveal, reveal_fn).bind(this), { margin: this.getAttribute("margin") ?? "-50px 0px" });
    }
  }
};
_reveal = new WeakSet();
reveal_fn = function() {
  this.style.opacity = "1";
  Motion.animate(
    this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children,
    { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] },
    { duration: 0.35, delay: Motion.stagger(0.05, { easing: "ease-out" }), easing: "ease" }
  );
};
if (!window.customElements.get("reveal-items")) {
  window.customElements.define("reveal-items", RevealItems);
}







  /**
   * =============================================================
   * ## ANNOUNCEMENT BAR
   * =============================================================
   */

  var AnnouncementBar = class extends CustomHTMLElement {
    connectedCallback() {
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this._updateCssVariables.bind(this));
        this.resizeObserver.observe(this);
      }
    }

    _updateCssVariables(entries) {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
          document.documentElement.style.setProperty("--announcement-bar-height", `${height}px`);
        }
      });
    }
  };
  window.customElements.define("announcement-bar", AnnouncementBar);










  /**
   * =============================================================
   * ## HEADER MAIN
   * =============================================================
   */

  var HeaderMain = class extends CustomHTMLElement {
    connectedCallback() {
      this.headerContent = this.querySelector(".header__content");
      /**
       * We store the header height in a CSS variable. This will update the 
       * CSS variables on window resizes.
       */
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this._updateCssVariables.bind(this));
        this.resizeObserver.observe(this);
        // this.resizeObserver.observe(this.headerContent);
      }

      if (this.isSticky) {
        this._onWindowScrollListener = throttle(this._checkScrollPosition.bind(this), 100);

          window.addEventListener("scroll", this._onWindowScrollListener);
      }

    }

    disconnectedCallback() {
      super.disconnectedCallback();
      
      if (window.ResizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.isSticky) {
        window.removeEventListener("scroll", this._onWindowScrollListener);
      }
    }

    get isSticky() {
      return this.hasAttribute("sticky");
    }

    get scrollThreshold() {
      return 150;
    }

    _updateCssVariables(entries) {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;

          document.documentElement.style.setProperty("--header-height", `${height}px`);
        }
      });
    }

    _checkScrollPosition(event) {
      if (window.scrollY > this.scrollThreshold) {
        this.parentElement.classList.add("header--reduced");
        document.querySelector(".main").classList.add("header--padding");
      } else {
        this.parentElement.classList.remove("header--reduced");
        document.querySelector(".main").classList.remove("header--padding");
      }
    }

  }

  window.customElements.define("header-main", HeaderMain);




  var PredictiveSearch = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();
      this.delegateElement = new Delegate(this);
      this.inputElement = this.querySelector('[name="q"]');

      this.delegateElement.on("input", '[name="q"]', this._debounce(this._onSearch.bind(this), 200));
    }

    set open(value) {
      if (value) {
        (async () => {
          await this._load();
          this.clientWidth;
          document.querySelector(".drawer--mobile-menu").removeAttribute("open");
          this.setAttribute("open", "");
        })();
      } else {
        this.removeAttribute("open");
        this.inputElement.value = "";
        this.dispatchEvent(new CustomEvent("predictive-search:close", {
          bubbles: true
        }));
      }
    }

    get focusTrap() {
      return this._focusTrap = this._focusTrap || focusTrap.createFocusTrap(this, {
        fallbackFocus: this,
        initialFocus: this.hasAttribute("initial-focus-selector") ? this.getAttribute("initial-focus-selector") : void 0,
        clickOutsideDeactivates: false,
        allowOutsideClick: true,
        returnFocusOnDeactivate: this.returnFocusOnDeactivate,
        onDeactivate: () => this.open = false,
        preventScroll: false
      });
    }

    async _onSearch(event, target) {
      if (event.key === "Enter") {
        return;
      }

      this.dispatchEvent(new CustomEvent("predictive-search:search", {
        bubbles: true,
        detail: {
          event: event,
          target: target
        }
      }));
    }

    _debounce(fn, delay3) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          fn.apply(this, args);
        }, delay3);
      };
    }
  }

  window.customElements.define("predictive-search", PredictiveSearch);







  var PredictiveSearchResults = class extends HTMLElement {
    connectedCallback() {
      this.loadingStateElement = this.querySelector(".predictive-search-results__loading-state");
      this.resultsElement = this.querySelector(".predictive-search-results__results");

      document.addEventListener("predictive-search:search", (event) => {
        let eventDetail = event.detail.event;
        let target = event.detail.target;
        this._onSearch(eventDetail, target)
      });

      document.addEventListener("predictive-search:close", (event) => {
        this.hidden = true;
        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = true;
      });
    }

    async _onSearch(event, target) {
      if (target.value === "") {
        this.hidden = true;
        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = true;
      } else {
        this.loadingStateElement.hidden = false;
        this.resultsElement.hidden = true;
        this.hidden = false;

        let searchResults = {};

        try {
          this.abortController = new AbortController();
            searchResults = await this._doPredictiveSearch(target.value);
        } catch (e) {
          if (e.name === "AbortError") {
            return;
          }
        }

        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = false;
        this.hidden = false;

        this.resultsElement.innerHTML = searchResults.html;
      }
    }

    async _doPredictiveSearch(term) {
      const response = await fetch(`${window.themeVariables.routes.predictiveSearchUrl}?q=${term}&resources[limit]=10&resources[type]=${window.themeVariables.settings.searchMode}&resources[options[unavailable_products]]=${window.themeVariables.settings.searchUnavailableProducts}&resources[options[fields]]=title,body,product_type,variants.title,variants.sku,vendor&section_id=predictive-search`, {
        signal: this.abortController.signal
      });

      const div = document.createElement("div");
      div.innerHTML = await response.text();

      return { 
        hasResults: div.querySelector(".predictive-search-results__results-inner") !== null, 
        html: div.firstElementChild.innerHTML 
      };
    }
  }

  window.customElements.define("predictive-search-results", PredictiveSearchResults);









  /**
   * =============================================================
   * ## DESKTOP NAVIGATION
   * =============================================================
   */

  var DesktopNavigation = class extends CustomHTMLElement {
    connectedCallback() {
      this.openingTimeout = null;
      this.currentMegaMenu = null;

      this.delegate.on("mouseenter", ".has-dropdown", (event, target) => {
        if (event.target === target && event.relatedTarget !== null) {
          this.openDropdown(target);
        }
      }, true);

      /**
       * Click event handlers for touch screens 
       */
      this.delegate.on("click", ".header__linklist-link[aria-expanded]", (event, target) => {

        event.preventDefault();

        if (window.matchMedia("(hover: hover)").matches) {
          location.href = target.getAttribute("href");
          return;
        }

        event.preventDefault();
        this.openDropdown(target.parentElement);
      });

      /**
       * Shopify theme editor events
       */
      this.delegate.on("shopify:block:select", (event) => this.openDropdown(event.target.parentElement));
      this.delegate.on("shopify:block:deselect", (event) => this.closeDropdown(event.target.parentElement));
    }

    openDropdown(parentElement) {
      const menuItem = parentElement.querySelector("[aria-controls]");
      const dropdown = parentElement.querySelector(`#${menuItem.getAttribute("aria-controls")}`);

      // Get current mega menu or set to null if it's a standard nav dropdown
      this.currentMegaMenu = dropdown.classList.contains("mega-menu") ? dropdown : null;

      let openingTimeout = setTimeout(() => {
        // If already open, then return
        if (menuItem.getAttribute("aria-expanded") === "true") {
          return;
        }

        // Set attributes for accessibility and CSS
        menuItem.setAttribute("aria-expanded", "true");
        dropdown.removeAttribute("hidden");

        const leaveListener = (event) => {
          if (event.relatedTarget !== null) {
            this.closeDropdown(parentElement);
            parentElement.removeEventListener("mouseleave", leaveListener);
          }
        };

        parentElement.addEventListener("mouseleave", leaveListener);
        openingTimeout = null;

        this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:open", { bubbles: true }));
      }, 100);

      parentElement.addEventListener("mouseleave", () => {
        if (openingTimeout) {
          clearTimeout(openingTimeout);
        }
      }, { once: true });
    }

    closeDropdown(parentElement) {
      const menuItem = parentElement.querySelector("[aria-controls]");
      const dropdown = parentElement.querySelector(`#${menuItem.getAttribute("aria-controls")}`);

      requestAnimationFrame(() => {

        // Set attributes for CSS and accessibility
        menuItem.setAttribute("aria-expanded", "false");

        setTimeout(() => {
          dropdown.setAttribute("hidden", "");
          clearTimeout(this.openingTimeout);
        }, dropdown.classList.contains("mega-menu") && this.currentMegaMenu !== dropdown ? 250 : 0);

        this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:close", { bubbles: true }));
      });
    }
  };

  window.customElements.define("desktop-navigation", DesktopNavigation);











  /**
   * =============================================================
   * ## MOBILE NAVIGATION
   * =============================================================
   */

  var MobileNavigation = class extends DrawerContent {
    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      
      switch (name) {
        case "open":
          triggerEvent(this, this.open ? "mobile-nav:open" : "mobile-nav:close");
      }
    }
  };

  window.customElements.define("mobile-navigation", MobileNavigation);










  /**
   * =============================================================
   * ## GESTURE AREA
   * =============================================================
   */

  var _domElement, _thresholdDistance, _thresholdTime, _signal, _firstClientX, _tracking, _start, _touchStart, touchStart_fn, _preventTouch, preventTouch_fn, _gestureStart, gestureStart_fn, _gestureMove, gestureMove_fn, _gestureEnd, gestureEnd_fn;
  var GestureArea = class {
    constructor(domElement, { thresholdDistance = 80, thresholdTime = 500, signal = null } = {}) {
      __privateAdd(this, _touchStart);
      __privateAdd(this, _preventTouch);
      __privateAdd(this, _gestureStart);
      __privateAdd(this, _gestureMove);
      __privateAdd(this, _gestureEnd);
      __privateAdd(this, _domElement, void 0);
      __privateAdd(this, _thresholdDistance, void 0);
      __privateAdd(this, _thresholdTime, void 0);
      __privateAdd(this, _signal, void 0);
      __privateAdd(this, _firstClientX, void 0);
      __privateAdd(this, _tracking, false);
      __privateAdd(this, _start, {});
      __privateSet(this, _domElement, domElement);
      __privateSet(this, _thresholdDistance, thresholdDistance);
      __privateSet(this, _thresholdTime, thresholdTime);
      __privateSet(this, _signal, signal);
      __privateGet(this, _domElement).addEventListener("touchstart", __privateMethod(this, _touchStart, touchStart_fn).bind(this), { passive: true, signal: __privateGet(this, _signal) });
      __privateGet(this, _domElement).addEventListener("touchmove", __privateMethod(this, _preventTouch, preventTouch_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
      __privateGet(this, _domElement).addEventListener("pointerdown", __privateMethod(this, _gestureStart, gestureStart_fn).bind(this), { signal: __privateGet(this, _signal) });
      __privateGet(this, _domElement).addEventListener("pointermove", __privateMethod(this, _gestureMove, gestureMove_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
      __privateGet(this, _domElement).addEventListener("pointerup", __privateMethod(this, _gestureEnd, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
      __privateGet(this, _domElement).addEventListener("pointerleave", __privateMethod(this, _gestureEnd, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
      __privateGet(this, _domElement).addEventListener("pointercancel", __privateMethod(this, _gestureEnd, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    }
  };

  _domElement = new WeakMap();
  _thresholdDistance = new WeakMap();
  _thresholdTime = new WeakMap();
  _signal = new WeakMap();
  _firstClientX = new WeakMap();
  _tracking = new WeakMap();
  _start = new WeakMap();
  _touchStart = new WeakSet();
  touchStart_fn = function(event) {
    __privateSet(this, _firstClientX, event.touches[0].clientX);
  };

  _preventTouch = new WeakSet();
  preventTouch_fn = function(event) {
    if (Math.abs(event.touches[0].clientX - __privateGet(this, _firstClientX)) > 10) {
      event.preventDefault();
    }
  };

  _gestureStart = new WeakSet();
  gestureStart_fn = function(event) {
    __privateSet(this, _tracking, true);
    __privateSet(this, _start, {
      time: new Date().getTime(),
      x: event.clientX,
      y: event.clientY
    });
  };

  _gestureMove = new WeakSet();
  gestureMove_fn = function(event) {
    if (__privateGet(this, _tracking)) {
      event.preventDefault();
    }
  };

  _gestureEnd = new WeakSet();

  gestureEnd_fn = function(event) {
    if (!__privateGet(this, _tracking)) {
      return;
    }
    __privateSet(this, _tracking, false);
    const now = new Date().getTime(), deltaTime = now - __privateGet(this, _start).time, deltaX = event.clientX - __privateGet(this, _start).x, deltaY = event.clientY - __privateGet(this, _start).y;
    if (deltaTime > __privateGet(this, _thresholdTime)) {
      return;
    }
    let matchedEvent;
    if (deltaX === 0 && deltaY === 0 && !event.target.matches("a, button, a :scope, button :scope")) {
      matchedEvent = "tap";
    } else if (deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
      matchedEvent = "swiperight";
    } else if (-deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
      matchedEvent = "swipeleft";
    } else if (deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
      matchedEvent = "swipedown";
    } else if (-deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
      matchedEvent = "swipeup";
    }
    if (matchedEvent) {
      __privateGet(this, _domElement).dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
    }
  };









  /**
   * =============================================================
   * ## Height Observer
   * - Used for product slideshow if using adaptive height
   * =============================================================
   */
  var HeightObserver = class extends HTMLElement {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(this._updateCustomProperties.bind(this)).observe(this);
      }
    }
    connectedCallback() {
      if (!window.ResizeObserver) {
        document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(1)}px`);
      }
    }
    _updateCustomProperties(entries) {
      requestAnimationFrame(() => {
        entries.forEach((entry) => {
          if (entry.target === this) {
            const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
            document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${height.toFixed(2)}px`);
          }
        });
      });
    }
  };
  if (!window.customElements.get("height-observer")) {
    window.customElements.define("height-observer", HeightObserver);
  }









  /**
   * =============================================================
   * ## SAFE STICKY
   * =============================================================
   */
  var _checkPositionListener, _initialTop, _lastKnownY, _currentTop, _position, _recalculateStyles, recalculateStyles_fn, _checkPosition, checkPosition_fn;

  var SafeSticky = class extends HTMLElement {
    constructor() {
      super(...arguments);
      __privateAdd(this, _recalculateStyles);
      __privateAdd(this, _checkPosition);
      __privateAdd(this, _checkPositionListener, throttle(__privateMethod(this, _checkPosition, checkPosition_fn).bind(this)));
      __privateAdd(this, _initialTop, 0);
      __privateAdd(this, _lastKnownY, 0);
      __privateAdd(this, _currentTop, 0);
      __privateAdd(this, _position, "relative");
    }

    connectedCallback() {
      __privateMethod(this, _recalculateStyles, recalculateStyles_fn).call(this);
      window.addEventListener("scroll", __privateGet(this, _checkPositionListener));
      new ResizeObserver(__privateMethod(this, _recalculateStyles, recalculateStyles_fn).bind(this)).observe(this);
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
    }
  };

  _checkPositionListener = new WeakMap();
  _initialTop = new WeakMap();
  _lastKnownY = new WeakMap();
  _currentTop = new WeakMap();
  _position = new WeakMap();
  _recalculateStyles = new WeakSet();

  recalculateStyles_fn = function() {
    this.style.removeProperty("top");
    const computedStyles = getComputedStyle(this);
    __privateSet(this, _initialTop, parseInt(computedStyles.top));
    __privateSet(this, _position, computedStyles.position);
  };

  _checkPosition = new WeakSet();

  checkPosition_fn = function() {
    if (__privateGet(this, _position) !== "sticky") {
      return this.style.removeProperty("top");
    }

    let bounds = this.getBoundingClientRect(), maxTop = bounds.top + window.scrollY - this.offsetTop + __privateGet(this, _initialTop), minTop = this.clientHeight - window.innerHeight + 20;

    if (window.scrollY < __privateGet(this, _lastKnownY)) {
      __privateSet(this, _currentTop, __privateGet(this, _currentTop) - (window.scrollY - __privateGet(this, _lastKnownY)));
    } else {
      __privateSet(this, _currentTop, __privateGet(this, _currentTop) + (__privateGet(this, _lastKnownY) - window.scrollY));
    }

    __privateSet(this, _currentTop, Math.min(Math.max(__privateGet(this, _currentTop), -minTop), maxTop, __privateGet(this, _initialTop)));
    __privateSet(this, _lastKnownY, window.scrollY);
    this.style.top = `${Math.round(__privateGet(this, _currentTop))}px`;
  };

  if (!window.customElements.get("safe-sticky")) {
    window.customElements.define("safe-sticky", SafeSticky);
  }









  /**
   * =============================================================
   * ## PAGE DOTS
   * =============================================================
   */
  var PageDots = class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();

      this.items = Array.from(this.children);
      this.items.forEach((button, index) => button.addEventListener("click", () => this.select(index), { signal: this._abortController.signal }));

      this.addEventListener("control:filter", this._filterItems, { signal: this._abortController.signal });

      if (this.controlledElement) {
        this.controlledElement.addEventListener("carousel:change", (event) => this.select(event.detail.index, false), { signal: this._abortController.signal });
      }

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target)));
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }

    get selectedIndex() {
      return this.items.findIndex((button) => button.getAttribute("aria-current") === "true");
    }

    select(selectedIndex, dispatchEvent = true) {

      if (this.hasAttribute("align-selected")) {
        this.scrollTo({
          left: this.items[selectedIndex].offsetLeft - this.clientWidth / 2 + this.items[selectedIndex].clientWidth / 2,
          top: this.items[selectedIndex].offsetTop - this.clientHeight / 2 - this.items[selectedIndex].clientHeight / 2,
          behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto"
        });
      }

      if (this.selectedIndex === selectedIndex) {
        return;
      }

      this.items.forEach((button, index) => button.setAttribute("aria-current", selectedIndex === index ? "true" : "false"));

      if (dispatchEvent) {
        this._dispatchEvent(selectedIndex);
      }
    }

    _filterItems(event) {
      this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
    }

    _dispatchEvent(index) {
      (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:select", { bubbles: true, cancelable: true, detail: { index } }));
    }
  };

  if (!window.customElements.get("page-dots")) {
    window.customElements.define("page-dots", PageDots);
  }









  /**
   * =============================================================
   * ## PREV BUTTON
   * =============================================================
   */

  var PrevButton = class extends HTMLButtonElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:prev", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });

      if (this.controlledElement) {
        this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "start", { signal: this._abortController.signal });

        this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "start" ? false : this.disabled, { signal: this._abortController.signal });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }
  };


  if (!window.customElements.get("prev-button")) {
    window.customElements.define("prev-button", PrevButton, { extends: "button" });
  }


  /**
   * =============================================================
   * ## NEXT BUTTON
   * =============================================================
   */

  var NextButton = class extends HTMLButtonElement {
    connectedCallback() {
      this._abortController = new AbortController();

      this.addEventListener("click", (event) => 
        (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:next", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });

      if (this.controlledElement) {
        this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "end", { signal: this._abortController.signal });

        this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "end" ? false : this.disabled, { signal: this._abortController.signal });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get controlledElement() {
      return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
    }
  };

  if (!window.customElements.get("next-button")) {
    window.customElements.define("next-button", NextButton, { extends: "button" });
  }









  /**
   * =============================================================
   * ## SCROLL AREA
   * =============================================================
   */

  var ScrollArea = class {
    constructor(element, abortController2 = null) {
      this._element = element;
      this._allowTriggerNearingStartEvent = false;
      this._allowTriggerLeavingStartEvent = true;
      this._allowTriggerNearingEndEvent = true;
      this._allowTriggerLeavingEndEvent = false;
      new ResizeObserver(throttle(this._checkIfScrollable.bind(this))).observe(element);
      this._element.addEventListener("scroll", throttle(this._onScroll.bind(this)), { signal: abortController2?.signal });
    }

    get scrollNearingThreshold() {
      return 125;
    }

    get scrollDirection() {
      if (this._element.scrollWidth > this._element.clientWidth) {
        return "inline";
      } else if (this._element.scrollHeight > this._element.clientHeight) {
        return "block";
      } else {
        return "none";
      }
    }

    _checkIfScrollable() {
      this._element.classList.toggle("is-scrollable", this.scrollDirection !== "none");
    }

    _onScroll() {
      clearTimeout(this._scrollTimeout);
      this._lastScrollPosition = this._lastScrollPosition ?? (this.scrollDirection === "inline" ? Math.abs(this._element.scrollLeft) : Math.abs(this._element.scrollTop));
      let direction;
      if (this.scrollDirection === "inline") {
        direction = this._lastScrollPosition > Math.abs(this._element.scrollLeft) ? "start" : "end";
        this._lastScrollPosition = Math.abs(this._element.scrollLeft);
      } else {
        direction = this._lastScrollPosition > Math.abs(this._element.scrollTop) ? "start" : "end";
        this._lastScrollPosition = Math.abs(this._element.scrollTop);
      }
      const scrollPosition = Math.round(Math.abs(this.scrollDirection === "inline" ? this._element.scrollLeft : this._element.scrollTop)), scrollMinusSize = Math.round(this.scrollDirection === "inline" ? this._element.scrollWidth - this._element.clientWidth : this._element.scrollHeight - this._element.clientHeight);
      if (direction === "start" && this._allowTriggerNearingStartEvent && scrollPosition <= this.scrollNearingThreshold) {
        this._allowTriggerNearingStartEvent = false;
        this._allowTriggerLeavingStartEvent = true;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "start" } }));
      } else if (direction === "end" && scrollPosition > this.scrollNearingThreshold) {
        this._allowTriggerNearingStartEvent = true;
        if (this._allowTriggerLeavingStartEvent) {
          this._allowTriggerLeavingStartEvent = false;
          this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "start" } }));
        }
      }
      if (direction === "end" && this._allowTriggerNearingEndEvent && scrollMinusSize <= scrollPosition + this.scrollNearingThreshold) {
        this._allowTriggerNearingEndEvent = false;
        this._allowTriggerLeavingEndEvent = true;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "end" } }));
      } else if (direction === "start" && scrollMinusSize > scrollPosition + this.scrollNearingThreshold) {
        this._allowTriggerNearingEndEvent = true;
        if (this._allowTriggerLeavingEndEvent) {
          this._allowTriggerLeavingEndEvent = false;
          this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "end" } }));
        }
      }
      if (window.onscrollend === void 0) {
        this._scrollTimeout = setTimeout(() => {
          this._element.dispatchEvent(new CustomEvent("scrollend", { bubbles: true, composed: true }));
        }, 75);
      }
    }
  };










  /**
   * =============================================================
   * ## SCROLL PROGRESS
   * =============================================================
   */
  var ScrollProgress = class extends HTMLElement {
    connectedCallback() {
      this.scrolledElement.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));
      if (window.ResizeObserver) {
        new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
      }
    }

    get scrolledElement() {
      return this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute("observes"));
    }

    _updateScrollProgress() {
      const scrollLeft = document.dir === "ltr" ? this.scrolledElement.scrollLeft : Math.abs(this.scrolledElement.scrollLeft), advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
      this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
    }
  };

  if (!window.customElements.get("scroll-progress")) {
    window.customElements.define("scroll-progress", ScrollProgress);
  }










  /**
   * =============================================================
   * ## SCROLL SHADOW
   * =============================================================
   */

  var template = `
    <style>
      :host {
        display: inline-block;
        contain: layout;
        position: relative;
      }
      
      :host([hidden]) {
        display: none;
      }
      
      s {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        background-image:
          var(--scroll-shadow-top, linear-gradient(to bottom, rgb(var(--color-bg)), rgb(var(--color-bg) / 0))),
          var(--scroll-shadow-bottom, linear-gradient(to top, rgb(var(--color-bg)), rgb(var(--color-bg) / 0))),
          var(--scroll-shadow-left, linear-gradient(to right, rgb(var(--color-bg)), rgb(var(--color-bg) / 0))),
          var(--scroll-shadow-right, linear-gradient(to left, rgb(var(--color-bg)), rgb(var(--color-bg) / 0)));
        background-position: top, bottom, left, right;
        background-repeat: no-repeat;
        background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
      }
    </style>
    <slot></slot>
    <s></s>
  `;

  var Updater = class {
    constructor(targetElement) {
      this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
    }

    start(element) {
      if (this.element) {
        this.stop();
      }
      if (element) {
        element.addEventListener("scroll", this.scheduleUpdate);
        this.resizeObserver.observe(element);
        this.element = element;
      }
    }

    stop() {
      if (!this.element) {
        return;
      }
      this.element.removeEventListener("scroll", this.scheduleUpdate);
      this.resizeObserver.unobserve(this.element);
      this.element = null;
    }

    update(targetElement, style) {
      if (!this.element) {
        return;
      }
      const maxSize = style.getPropertyValue("--scroll-shadow-size") ? parseInt(style.getPropertyValue("--scroll-shadow-size")) : 0;
      const scroll = {
        top: Math.max(this.element.scrollTop, 0),
        bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
        left: Math.max(this.element.scrollLeft, 0),
        right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
      };
      requestAnimationFrame(() => {
        for (const position of ["top", "bottom", "left", "right"]) {
          targetElement.style.setProperty(
            `--${position}`,
            `${scroll[position] > maxSize ? maxSize : scroll[position]}px`
          );
        }
      });
    }
  };

  var ScrollShadow = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).innerHTML = template;
      this.updater = new Updater(this.shadowRoot.lastElementChild);
    }

    connectedCallback() {
      this.shadowRoot.querySelector("slot").addEventListener("slotchange", this.start);
      this.start();
    }

    disconnectedCallback() {
      this.updater.stop();
    }

    start() {
      if (this.firstElementChild) {
        this.updater.start(this.firstElementChild);
      }
    }
  };

  if ("ResizeObserver" in window && !window.customElements.get("scroll-shadow")) {
    window.customElements.define("scroll-shadow", ScrollShadow);
  }











  /**
   * =============================================================
   * ## BASE CAROUSEL
   * =============================================================
   */

  var BaseCarousel = class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();

      this._reloaded = false;

      if (Shopify.designMode) {
        this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => this._reloaded = event.detail.load);
      }

      if (this.items.length > 1) {
        if (Shopify.designMode) {
          this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target), { animate: !event.detail.load }));
        }

        if (this.hasAttribute("adaptive-height")) {
          this.addEventListener("carousel:settle", this._adjustHeight);
          this._adjustHeight();
        }

        this.addEventListener("carousel:select", this._preloadImages, { signal: this._abortController.signal });
        this.addEventListener("carousel:filter", this._filterItems, { signal: this._abortController.signal });
        this.addEventListener("control:prev", this.previous, { signal: this._abortController.signal });
        this.addEventListener("control:next", this.next, { signal: this._abortController.signal });
        this.addEventListener("control:select", (event) => this.select(event.detail.index), { signal: this._abortController.signal });
      }

      if (this.selectedIndex === 0) {
        this._dispatchEvent("carousel:select", 0);
      } else {
        this.select(this.selectedIndex, { animate: false, force: true });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get items() {
      if (this.hasAttribute("reversed")) {
        return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children).reverse();
      } else {
        return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children);
      }
    }

    get visibleItems() {
      return this.items.filter((item) => item.offsetParent !== null);
    }

    get selectedIndex() {
      return this._selectedIndex = this._selectedIndex ?? parseInt(this.getAttribute("initial-index") || 0);
    }

    get selectedIndexAmongVisible() {
      return this.visibleItems.indexOf(this.selectedSlide);
    }

    get loop() {
      return false;
    }

    get selectedSlide() {
      return this.items[this.selectedIndex];
    }

    get previousSlide() {
      return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible - 1 + this.visibleItems.length) % this.visibleItems.length : Math.max(this.selectedIndexAmongVisible - 1, 0)];
    }

    get nextSlide() {
      return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible + 1 + this.visibleItems.length) % this.visibleItems.length : Math.min(this.selectedIndexAmongVisible + 1, this.visibleItems.length - 1)];
    }

    previous(animate11 = true) {
      this.select(this.items.indexOf(this.previousSlide), { direction: "previous", animate: animate11 });
    }

    next(animate11 = true) {
      this.select(this.items.indexOf(this.nextSlide), { direction: "next", animate: animate11 });
    }

    _transitionTo(fromSlide, toSlide, options = {}) {
    }

    _adjustHeight() {
      if (this.hasAttribute("adaptive-height") && this.selectedSlide.clientHeight !== this.clientHeight) {
        this.style.maxHeight = `${this.selectedSlide.clientHeight}px`;
      }
    }

    _filterItems(event) {
      this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
    }

    _preloadImages() {
      [this.previousSlide, this.nextSlide].forEach((item) => {
        if (item) {
          Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.setAttribute("loading", "eager"));
        }
      });
    }

    _dispatchEvent(eventName, index) {
      this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { slide: this.items[index], index } }));
    }
  };










  /**
   * =============================================================
   * ## SCROLL CAROUSEL
   * =============================================================
   */

  var ScrollCarousel = class extends BaseCarousel {
    constructor() {
      super();

      if (window.ResizeObserver) {
        new ResizeObserver(throttle(this._adjustHeight.bind(this))).observe(this);
      }
    }

    connectedCallback() {
      this._hasPendingProgrammaticScroll = false;
      this._scrollArea = new ScrollArea(this, this._abortController);

      super.connectedCallback();

      this.addEventListener("scroll", throttle(this._onCarouselScroll.bind(this)), { signal: this._abortController.signal });
      this.addEventListener("scrollend", this._onScrollSettled, { signal: this._abortController.signal });
    }

    get itemOffset() {
      return this.visibleItems.length < 2 ? 0 : this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
    }

    get slidesPerPage() {
      return this.visibleItems.length < 2 ? 1 : Math.floor((this.clientWidth - this.visibleItems[0].offsetLeft) / (Math.abs(this.itemOffset) - (parseInt(getComputedStyle(this).gap) || 0)));
    }

    get totalPages() {
      return this.visibleItems.length < 2 ? 1 : this.visibleItems.length - this.slidesPerPage + 1;
    }

    select(index, { animate: animate11 = true, force = false } = {}) {
      const indexBeforeChange = this.selectedIndex;

      if (!this.offsetParent || this._scrollArea.scrollDirection === "none") {
        return this._selectedIndex = index;
      }

      const indexAmongVisible = this.visibleItems.indexOf(this.items[index]);
      
      index = this.items.indexOf(this.visibleItems[Math.min(this.totalPages, indexAmongVisible)]);

      this._selectedIndex = index;

      this._dispatchEvent("carousel:select", index);

      if (index !== indexBeforeChange || force) {
        const [fromSlide, toSlide] = [this.items[indexBeforeChange], this.items[index]];
        this._dispatchEvent("carousel:change", index);
        this._transitionTo(fromSlide, toSlide, { animate: animate11 });
      }
    }

    _transitionTo(fromSlide, toSlide, { animate: animate11 = true } = {}) {
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");
      let slideAlign = this._extractSlideAlign(toSlide), scrollAmount = 0;
      switch (slideAlign) {
        case "start":
          scrollAmount = this.itemOffset * this.items.indexOf(toSlide);
          break;
        case "center":
          scrollAmount = toSlide.offsetLeft - (this.clientWidth / 2 - (parseInt(getComputedStyle(this).scrollPaddingInline) || 0)) + toSlide.clientWidth / 2;
          break;
      }

      this._hasPendingProgrammaticScroll = animate11;
      this.scrollTo({ left: scrollAmount, behavior: animate11 ? "smooth" : "auto" });
    }

    _onCarouselScroll() {
      if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === "none") {
        return;
      }

      const newIndex = this.items.indexOf(this.visibleItems[Math.round(this.scrollLeft / this.itemOffset)]);

      if (newIndex !== this.selectedIndex) {
        this._selectedIndex = newIndex;
        this._dispatchEvent("carousel:select", this.selectedIndex);
        this._dispatchEvent("carousel:change", this.selectedIndex);
      }
    }

    _onScrollSettled() {
      this._hasPendingProgrammaticScroll = false;
      this._dispatchEvent("carousel:settle", this.selectedIndex);
    }

    _adjustHeight() {
      this.style.maxHeight = null;
      if (this._scrollArea.scrollDirection !== "none") {
        super._adjustHeight();
      }
    }

    _extractSlideAlign(slide) {
      return getComputedStyle(slide).scrollSnapAlign === "center" ? "center" : "start";
    }
  };

  if (!window.customElements.get("scroll-carousel")) {
    window.customElements.define("scroll-carousel", ScrollCarousel);
  }










  /**
   * =============================================================
   * ## PRODUCT GALLERY 
   * =============================================================
   */

  var ProductGallery = class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      var _a;

      var form = document.getElementById(this.getAttribute("form-id"));
            (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));

      this._carousels = Array.from(this.querySelectorAll("media-carousel"));
      this._viewInSpaceButton = this.querySelector("[data-shopify-xr]");
      this.addEventListener("carousel:change", this._onCarouselChanged);

      if (this._viewInSpaceButton) {
        this.addEventListener("carousel:settle", this._updateViewInSpaceButton);
      }

      if (this.hasAttribute("enable-zoom")) {
        this.delegateGallery = new Delegate(this);

        this.delegateGallery.on('click', '[data-zoom-open]', this._openModal.bind(this));
      }
    }

    _openModal(event, target) {
      let productZoomModal = document.getElementById(target.getAttribute("aria-controls"));
      let mediaId = target.getAttribute("data-media-id");

      let zoomMedia = Array.from(productZoomModal.querySelectorAll(".product-zoom__media"));

      zoomMedia.forEach((media) => {
        if (media.getAttribute("data-media-id") == mediaId) {
          media.style.display = "block";
        } else {
          media.style.display = "none";
        }
      });

      productZoomModal.open = true;
    }

    _updateViewInSpaceButton(event) {
      if (event.detail.slide.getAttribute("data-media-type") === "model") {
        this._viewInSpaceButton.setAttribute("data-shopify-model3d-id", event.detail.slide.getAttribute("data-media-id"));
      } else {
        this._viewInSpaceButton.setAttribute("data-shopify-model3d-id", this._viewInSpaceButton.getAttribute("data-shopify-model3d-default-id"));
      }
    }

    _onCarouselChanged(event) {
    }

    _onVariantChanged(event) {
      const filteredIndexes = this._getFilteredMediaIndexes(event.detail.product, event.detail.variant);

      this._carousels.forEach((carousel) => carousel.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes } })));

      if (event.detail.variant["featured_media"] && event.detail.previousVariant["featured_media"] && event.detail.previousVariant["featured_media"]["id"] !== event.detail.variant["featured_media"]["id"] || !event.detail.previousVariant["featured_media"] && event.detail.variant["featured_media"]) {
        this._carousels.forEach((carousel) => carousel.select(event.detail.variant["featured_media"]["position"] - 1, { animate: false }));
      }
    }

    _getFilteredMediaIndexes(product, variant) {
      const filteredMediaIds = [];

      product["media"].forEach((media) => {
        let matchMedia = variant["featured_media"] && media["position"] === variant["featured_media"]["position"];

        if (media["alt"]?.includes("#") && media["alt"] !== product["title"]) {
          if (!matchMedia) {
            const altParts = media["alt"].split("#"), mediaGroupParts = altParts.pop().split("_");

            product["options"].forEach((option) => {
              if (option["name"].toLowerCase() === mediaGroupParts[0].toLowerCase()) {
                if (variant["options"][option["position"] - 1].toLowerCase() !== mediaGroupParts[1].trim().toLowerCase()) {
                  filteredMediaIds.push(media["position"] - 1);
                }
              }
            });
          }
        }
      });
      return filteredMediaIds;
    }
  };

  if (!window.customElements.get("product-gallery")) {
    window.customElements.define("product-gallery", ProductGallery);
  }



  var ProductGallerySecondary = class extends ProductGallery {
    connectedCallback() {
      super.connectedCallback();
    }
  };

  if (!window.customElements.get("product-gallery-secondary")) {
    window.customElements.define("product-gallery-secondary", ProductGallerySecondary);
  }










  /**
   * =============================================================
   * ## MEDIA CAROUSEL
   * =============================================================
   */

  var MediaCarousel = class extends ScrollCarousel {
    connectedCallback() {
      super.connectedCallback();

      this._onGestureChangedListener = this._onGestureChanged.bind(this);

      this.addEventListener("gesturestart", this._onGestureStart, { capture: false, signal: this._abortController.signal });

      this.addEventListener("carousel:settle", this._onMediaSettled, { signal: this._abortController.signal });
    }

    _onMediaSettled(event) {
      const media = event.detail.slide;

      this.items.filter((item) => ["video", "external_video", "model"].includes(item.getAttribute("data-media-type"))).forEach((item) => item.firstElementChild.pause());

      switch (media.getAttribute("data-media-type")) {
        case "external_video":
        case "video":
          if (this.hasAttribute("autoplay")) {
            media.firstElementChild.play();
          }
          break;
        case "model":
          if (window.matchMedia("(min-width: 1025px)").matches) {
            media.firstElementChild.play();
          }
          break;
      }
    }

    _onGalleryClick(event) {
      if (event.target.matches("button, a[href], button :scope, a[href] :scope") || !window.matchMedia("screen and (pointer: fine)").matches) {
        return;
      }

      if (this.selectedSlide.getAttribute("data-media-type") !== "image") {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect(), offsetX = event.clientX - rect.left;
      offsetX > this.clientWidth / 2 ? this.next() : this.previous();
    }

    _onGestureStart(event) {
      event.preventDefault();
      this.addEventListener("gesturechange", this._onGestureChangedListener, { capture: false, signal: this._abortController.signal });
    }

    _onGestureChanged(event) {
      event.preventDefault();
      if (event.scale > 1.5) {
        this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: this.selectedIndexAmongVisible } }));
        this.removeEventListener("gesturechange", this._onGestureChangedListener);
      }
    }
  };

  if (!window.customElements.get("media-carousel")) {
    window.customElements.define("media-carousel", MediaCarousel);
  }








  /**
   * =============================================================
   * ## PRODUCT ZOOM MODAL
   * =============================================================
   */

  var ProductZoomModal = class extends ModalContent {
    connectedCallback() {
      super.connectedCallback();
      this.delegateElement = new Delegate(this);
      this.delegateElement.on('click', '[data-zoom-close]', this._closeModal.bind(this));
    }

    _closeModal(event, target) {
      this.open = false;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-all", this.open);

          // Ensure we scroll to top of modal when opened
          this.querySelector(".modal__inner").scrollTo({
            behavior: 'auto',
            top: 0
          });
      }
    }
  }

  window.customElements.define("product-zoom-modal", ProductZoomModal);








  /**
   * =============================================================
   * ## PRODUCT PRICE
   * =============================================================
   */

  var ProductPrice = class extends HTMLElement {
    connectedCallback() {
      var _a;
      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));
    }

    _onVariantChanged(event) {
      this._updatePrices(event.detail.variant);
    }

    get isTradeCustomer() {
      return this.hasAttribute("trade-customer");
    }

    get tradeDiscount() {
      return this.getAttribute("trade-discount");
    }

    _updatePrices(variant) {
      let productPrices = this.querySelector("[data-product-price-list]");
      let currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;

      if (!productPrices) {
        return;
      }

      if (!variant) {
        productPrices.style.display = "none";
      } else {
        productPrices.innerHTML = "";

        if (this.isTradeCustomer && this.tradeDiscount > 0) {
          console.log("We are trade and over:" + this.tradeDiscount);
          
          let discountAmount = (this.tradeDiscount / 100) * variant["price"];
          let tradePrice = variant["price"] - discountAmount;
          
          console.log(discountAmount);
          console.log(tradePrice);
          productPrices.innerHTML += `<span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(variant["price"], currencyFormat)}</span>`;

          productPrices.innerHTML += `<span class="price price--highlight"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(tradePrice, currencyFormat)}</span>`;
        } else {
          // If we have a sale price
          if (variant["compare_at_price"] > variant["price"]) {
            productPrices.innerHTML += `<span class="price price--highlight"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant["price"], currencyFormat)}</span>`;

            productPrices.innerHTML += `<span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(variant["compare_at_price"], currencyFormat)}</span>`;
          } else {
            productPrices.innerHTML += `<span class="price"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant["price"], currencyFormat)}</span>`;
          }
        }

        productPrices.style.display = "";
      }
    }
  };

  window.customElements.define("product-price", ProductPrice);










  /**
   * =============================================================
   * ## PRODUCT VARIANTS
   * =============================================================
   */

  var ProductVariants = class extends CustomHTMLElement {

    async connectedCallback() {
      this.masterSelector = document.getElementById(this.getAttribute("form-id")).id;
      this.optionSelectors = Array.from(this.querySelectorAll("[data-selector-type]"));

      if (!this.masterSelector) {
        console.warn(`The variant selector for product with handle ${this.productHandle} is not linked to any product form.`);
        return;
      }

      this.product = await ProductLoader.load(this.productHandle);

      this.delegate.on("change", '[name^="option"]', this._onOptionChanged.bind(this));
      this.masterSelector.addEventListener("change", this._onMasterSelectorChanged.bind(this));

      this._updateDisableSelectors();
      this.selectVariant(this.selectedVariant["id"]);
    }

    get selectedVariant() {
      return this._getVariantById(parseInt(this.masterSelector.value));
    }

    get productHandle() {
      return this.getAttribute("handle");
    }

    get hideSoldOutVariants() {
      return this.hasAttribute("hide-sold-out-variants");
    }

    get updateUrl() {
      return this.hasAttribute("update-url");
    }

    selectVariant(id) {
      var _a;

      if (!this._isVariantSelectable(this._getVariantById(id))) {
        id = this._getFirstMatchingAvailableVariant()["id"];
      }

      if (((_a = this.selectedVariant) == null ? void 0 : _a.id) === id) {
        return;
      }

      const previousVariant = this.selectedVariant;
      if (previousVariant && previousVariant.id === id) {
        return;
      }

      this.masterSelector.value = id;

      this.masterSelector.dispatchEvent(new Event("change", { bubbles: true }));

      if (this.updateUrl && history.replaceState) {
        const newUrl = new URL(window.location.href);
        if (id) {
          newUrl.searchParams.set("variant", id);
        } else {
          newUrl.searchParams.delete("variant");
        }
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }

      this._updateDisableSelectors();

      this.masterSelector.form.dispatchEvent(new CustomEvent("variant:changed", {
        bubbles: true,
        detail: {
          product: this.product,
          variant: this.selectedVariant, previousVariant
        }
      }));
    }

    _onOptionChanged() {
      var _a;
      this.selectVariant((_a = this._getVariantFromOptions()) == null ? void 0 : _a.id);
    }

    _onMasterSelectorChanged() {
      var _a;

      const options = ((_a = this.selectedVariant) == null ? void 0 : _a.options) || [];

      options.forEach((value, index) => {
        let input = this.querySelector(`input[name="option${index + 1}"][value="${CSS.escape(value)}"], select[name="option${index + 1}"]`); 
        let triggerChangeEvent = false;

        if (input.tagName === "SELECT") {
          triggerChangeEvent = input.value !== value;
          input.value = value;
        } else if (input.tagName === "INPUT") {
          triggerChangeEvent = !input.checked && input.value === value;
          input.checked = input.value === value;
        }

        if (triggerChangeEvent) {
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    _getVariantById(id) {
      return this.product["variants"].find((variant) => variant["id"] === id);
    }

    _getVariantFromOptions() {
      const options = this._getSelectedOptionValues();

      return this.product["variants"].find((variant) => {
        return variant["options"].every((value, index) => value === options[index]);
      });
    }

    _isVariantSelectable(variant) {
      if (!variant) {
        return false;
      } else {
        return variant["available"] || !this.hideSoldOutVariants && !variant["available"];
      }
    }

    _getFirstMatchingAvailableVariant() {
      let options = this._getSelectedOptionValues();
      let matchedVariant = null; 
      let slicedCount = 0;

      do {
        options.pop();
        slicedCount += 1;
        matchedVariant = this.product["variants"].find((variant) => {
          return variant["available"] && variant["options"].slice(0, variant["options"].length - slicedCount).every((value, index) => value === options[index]);
        });
      } while (!matchedVariant && options.length > 0);

      return matchedVariant;
    }

    _getSelectedOptionValues() {
      const options = [];

      Array.from(this.querySelectorAll('input[name^="option"]:checked, select[name^="option"]')).forEach((option) => options.push(option.value));
      return options;
    }

    _updateDisableSelectors() {
      const selectedVariant = this.selectedVariant;

      if (!selectedVariant) {
        return;
      }

      const applyClassToSelector = (selector, valueIndex, available, hasAtLeastOneCombination) => {
        let selectorType = selector.getAttribute("data-selector-type"), cssSelector = "";

        switch (selectorType) {
          case "color":
            cssSelector = `.color-swatch:nth-child(${valueIndex + 1})`;
            break;
          case "variant-image":
            cssSelector = `.variant-swatch:nth-child(${valueIndex + 1})`;
            break;
          case "block":
            cssSelector = `.block-swatch:nth-child(${valueIndex + 1})`;
            break;
          case "dropdown":
            cssSelector = `.combo-box__option-item:nth-child(${valueIndex + 1})`;
            break;
        }

        selector.querySelector(cssSelector).toggleAttribute("hidden", !hasAtLeastOneCombination);

        if (this.hideSoldOutVariants) {
          selector.querySelector(cssSelector).toggleAttribute("hidden", !available);
        } else {
          selector.querySelector(cssSelector).classList.toggle("is-disabled", !available);
        }
      };

      if (this.optionSelectors && this.optionSelectors[0]) {
        this.product["options"][0]["values"].forEach((value, valueIndex) => {

          const hasAtLeastOneCombination = this.product["variants"].some((variant) => variant["option1"] === value && variant);
          const hasAvailableVariant = this.product["variants"].some((variant) => variant["option1"] === value && variant["available"]);

          applyClassToSelector(this.optionSelectors[0], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);

          if (this.optionSelectors[1]) {
            this.product["options"][1]["values"].forEach((value2, valueIndex2) => {
              const hasAtLeastOneCombination2 = this.product["variants"].some((variant) => variant["option2"] === value2 && variant["option1"] === selectedVariant["option1"] && variant);

              const hasAvailableVariant2 = this.product["variants"].some((variant) => variant["option2"] === value2 && variant["option1"] === selectedVariant["option1"] && variant["available"]);

              applyClassToSelector(this.optionSelectors[1], valueIndex2, hasAvailableVariant2, hasAtLeastOneCombination2);

              if (this.optionSelectors[2]) {
                this.product["options"][2]["values"].forEach((value3, valueIndex3) => {
                  const hasAtLeastOneCombination3 = this.product["variants"].some((variant) => variant["option3"] === value3 && variant["option1"] === selectedVariant["option1"] && variant["option2"] === selectedVariant["option2"] && variant);
                  
                  const hasAvailableVariant3 = this.product["variants"].some((variant) => variant["option3"] === value3 && variant["option1"] === selectedVariant["option1"] && variant["option2"] === selectedVariant["option2"] && variant["available"]);

                  applyClassToSelector(this.optionSelectors[2], valueIndex3, hasAvailableVariant3, hasAtLeastOneCombination3);
                });
              }
            });
          }
        });
      }
    }
  };

  window.customElements.define("product-variants", ProductVariants);










  /**
   * =============================================================
   * ## PRODUCT PAYMENT CONTAINER
   * =============================================================
   */
  var PaymentContainer = class extends HTMLElement {
    connectedCallback() {
      var _a;

      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));

      if (Shopify.designMode && Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
      }
    }

    _onVariantChanged(event) {
      this._updateAddToCartButton(event.detail.variant);
    }

    _updateAddToCartButton(variant) {
      let addToCartButtonElement = this.querySelector("[data-product-add-to-cart-button]");

      if (!addToCartButtonElement) {
        return;
      }

      let addToCartButtonText = "";

      addToCartButtonElement.classList.remove("button--primary", "button--secondary", "button--ternary");

      if (!variant) {
        addToCartButtonElement.setAttribute("disabled", "disabled");
        addToCartButtonElement.classList.add("button--ternary");
        addToCartButtonText = window.themeVariables.strings.productFormUnavailable;
      } else {
        if (variant["available"]) {
          addToCartButtonElement.removeAttribute("disabled");
          addToCartButtonElement.classList.add(addToCartButtonElement.hasAttribute("data-use-primary") ? "button--primary" : "button--secondary");
          addToCartButtonText = addToCartButtonElement.getAttribute("data-button-content");
        } else {
          addToCartButtonElement.setAttribute("disabled", "disabled");
          addToCartButtonElement.classList.add("button--ternary");
          addToCartButtonText = window.themeVariables.strings.productFormSoldOut;
        }
      }

      if (addToCartButtonElement.getAttribute("is") === "loader-button") {
        addToCartButtonElement.firstElementChild.innerHTML = addToCartButtonText;
      } else {
        addToCartButtonElement.innerHTML = addToCartButtonText;
      }
    }
  };

  window.customElements.define("product-payment-container", PaymentContainer);










  /**
   * =============================================================
   * ## PRODUCT FORM
   * =============================================================
   */
  var ProductForm = class extends HTMLFormElement {
    connectedCallback() {
      this.id.disabled = false;

      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        return;
      }

      this.addEventListener("submit", this._onSubmit.bind(this));
    }

    async _onSubmit(event) {
      event.preventDefault();

      if (!this.checkValidity()) {
        this.reportValidity();
        return;
      }

      const submitButtons = Array.from(this.elements).filter((button) => button.type === "submit");
      submitButtons.forEach((submitButton) => {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-busy", "true");
      });

      const productForm = new FormData(this);

      productForm.append("sections", ["mini-cart"]);;

      productForm.delete("option1");
      productForm.delete("option2");
      productForm.delete("option3");

      const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        body: productForm,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");
      });

      const responseJson = await response.json();

      if (response.ok) {
        this.dispatchEvent(new CustomEvent("variant:added", {
          bubbles: true,
          detail: {
            variant: responseJson.hasOwnProperty("items") ? responseJson["items"][0] : responseJson
          }
        }));

        fetch(`${window.themeVariables.routes.cartUrl}.js`).then(async (response2) => {

          const cartContent = await response2.json();

          document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));
        });

        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: responseJson,
            openMiniCart: window.themeVariables.settings.cartType === "drawer"
            // openMiniCart: window.themeVariables.settings.cartType === "drawer" && this.closest(".drawer") === null,
            // element: this.closest(".drawer")
          }
        }));
      }

      this.dispatchEvent(new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: response.ok ? "success" : "error",
          error: responseJson["description"] || "",
          quantityAdded: response.ok ? document.querySelector('[name="quantity"]').value : "",
          productAdded: response.ok ? responseJson["product_title"] : ""
        }
      }));
    }
  };
  window.customElements.define("product-form", ProductForm, { extends: "form" });








  /**
   * =============================================================
   * ## PRODUCT VARIATIONS
   * =============================================================
   */
  var ProductVariations = class extends HTMLElement {
    connectedCallback() {
      this.delegateElement = new Delegate(this);
      this.selectedValue = this.querySelector(".selected-value");
      this.swatches = Array.from(this.querySelectorAll(".media-swatch"));
      this.activeValue = this.querySelector(".media-swatch.is-selected .visually-hidden").innerText;
      this.delegateElement.on("mouseover", '.media-swatch', this._onSwatchHovered.bind(this), true);
      this.delegateElement.on("mouseout", '.media-swatch', this._onSwatchReset.bind(this), true);
    }
  
    _onSwatchHovered(event, target) {
      const swatchName = target.querySelector('.visually-hidden').innerText;
      this.selectedValue.innerText = swatchName;
    }
  
    _onSwatchReset(event, target) {
      this.selectedValue.innerText = this.activeValue;
    }
  };
  
  if (!window.customElements.get("product-variations")) {
    window.customElements.define("product-variations", ProductVariations);
  }








  /**
   * =============================================================
   * ## CART COUNT
   * =============================================================
   */
  var CartCount = class extends CustomHTMLElement {
    connectedCallback() {
      this.rootDelegate.on("cart:updated", (event) => this.innerText = "(" + event.detail.cart["item_count"] + ")" );
    }
  };

  window.customElements.define("cart-count", CartCount);










  /**
   * =============================================================
   * ## CART DOT
   * =============================================================
   */
  var CartDot = class extends CustomHTMLElement {
    connectedCallback() {
      this.rootDelegate.on("cart:updated", (event) => {
        if (event.detail.cart["item_count"] > 0) {
          this.style.display = "block";
        } else {
          this.style.display = "none";
        }
      });
    }
  };

  window.customElements.define("cart-dot", CartDot);










  /**
   * =============================================================
   * ## CART NOTE
   * =============================================================
   */
  var CartNote = class extends HTMLTextAreaElement {
    connectedCallback() {
      this.addEventListener("change", this._onNoteChanged.bind(this));
    }

    get ownedToggle() {
      return this.hasAttribute("aria-owns") ? document.getElementById(this.getAttribute("aria-owns")) : null;
    }

    async _onNoteChanged() {
      if (this.ownedToggle) {
        this.ownedToggle.innerHTML = this.value === "" ? window.themeVariables.strings.cartAddOrderNote : window.themeVariables.strings.cartEditOrderNote;
      }

      const response = await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
        body: JSON.stringify({ note: this.value }),
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      const cartContent = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {
          cart: cartContent
        }
      }));
    }
  };
  window.customElements.define("cart-note", CartNote, { extends: "textarea" });










  /**
   * =============================================================
   * ## LINE ITEM
   * =============================================================
   */

  var LineItem = class extends HTMLElement {
    connectedCallback() {
      this.lineItemLoader = this.querySelector(".line-item__loader");
      this.addEventListener("line-item-quantity:change:start", this._onQuantityStart.bind(this));
      this.addEventListener("line-item-quantity:change:end", this._onQuantityEnd.bind(this));
    }

    _onQuantityStart() {
      if (!this.lineItemLoader || !supportsAnimationsAPI) {
        return;
      }

      this.lineItemLoader.hidden = false;
      this.lineItemLoader.firstElementChild.hidden = false;
      this.lineItemLoader.lastElementChild.hidden = true;
    }

    async _onQuantityEnd(event) {
      if (event.detail.cart["item_count"] === 0) {
        return;
      }

      if (this.lineItemLoader && supportsAnimationsAPI) {
        await this.lineItemLoader.firstElementChild.animate({ opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, 75).finished;

        this.lineItemLoader.firstElementChild.hidden = true;

        if (event.detail.newLineQuantity === 0) {
          await this.animate({ opacity: [1, 0], height: [`${this.clientHeight}px`, 0] }, { duration: 300, easing: "ease" }).finished;

          this.remove();
        } else {
          this.lineItemLoader.lastElementChild.hidden = false;

          await this.lineItemLoader.lastElementChild.animate({ opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 75, endDelay: 300 }).finished;

          this.lineItemLoader.hidden = true;
        }
      }
    }
  };

  window.customElements.define("line-item", LineItem);










  /**
   * =============================================================
   * ## LINE ITEM QUANTITY
   * =============================================================
   */
  var LineItemQuantity = class extends CustomHTMLElement {
    connectedCallback() {
      this.delegate.on("click", "a", this._onQuantityLinkClicked.bind(this));
      this.delegate.on("change", "input", this._onQuantityChanged.bind(this));
    }

    _onQuantityLinkClicked(event, target) {
      event.preventDefault();
      this._updateFromLink(target.href);
    }

    _onQuantityChanged(event, target) {
      this._updateFromLink(`${window.themeVariables.routes.cartChangeUrl}?quantity=${target.value}&line=${target.getAttribute("data-line")}`);
    }

    async _updateFromLink(link) {
      if (window.themeVariables.settings.pageType === "cart") {
        window.location.href = link;
        return;
      }

      const changeUrl = new URL(link, `https://${window.themeVariables.routes.host}`); 
      const searchParams = changeUrl.searchParams; 
      const line = searchParams.get("line"); 
      const id = searchParams.get("id"); 
      const quantity = parseInt(searchParams.get("quantity"));

      this.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: quantity } }));
      const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
        body: JSON.stringify({ line, id, quantity, sections: ["mini-cart"] }),
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      const cartContent = await response.json();

      this.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {
          cart: cartContent
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart: cartContent,
          replacementDelay: quantity === 0 ? 600 : 750
        }
      }));
    }
  };
  
  window.customElements.define("line-item-quantity", LineItemQuantity);










  /**
   * =============================================================
   * ## CART NOTIFICATION
   * =============================================================
   */
  var CartNotification = class extends CustomHTMLElement {
    connectedCallback() {
      this.rootDelegate.on("cart-notification:show", this._onShow.bind(this), !this.hasAttribute("global"));
      this.delegate.on("click", '[data-action="close"]', (event) => {
        event.stopPropagation();
        this.hidden = true;
      });

      this.addEventListener("mouseenter", this.stopTimer.bind(this));
      this.addEventListener("mouseleave", this.startTimer.bind(this));
      window.addEventListener("pagehide", () => this.hidden = true);
    }

    set hidden(value) {
      if (!value) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
      this.toggleAttribute("hidden", value);
    }

    get isInsideDrawer() {
      return this.classList.contains("cart-notification--drawer");
    }

    stopTimer() {
      clearTimeout(this._timeout);
    }

    startTimer() {
      this._timeout = setTimeout(() => this.hidden = true, 3e3);
    }

    _onShow(event) {

      if (this.isInsideDrawer && !this.closest(".drawer").open) {
        return;
      }

      if (this.hasAttribute("global") && event.detail.status === "success" && window.themeVariables.settings.cartType === "drawer") {
        return;
      }

      event.stopPropagation();

      let closeButtonHtml = "";

      if (!this.isInsideDrawer) {
        closeButtonHtml = `
        <button class="cart-notification__close o-tap-area" data-action="close">
          <span class="visually-hidden">${window.themeVariables.strings.accessibilityClose}</span>
          <svg focusable="false" width="14" height="14" class="icon icon--close icon--inline" viewBox="0 0 14 14">
            <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path>
          </svg>
        </button>
      `;
      }

      if (event.detail.status === "success") {
        this.classList.remove("cart-notification--error");
        this.innerHTML = `
        <div class="cart-notification__overflow">
          <div class="cart-notification__wrapper">
            <div class="cart-notification__text-wrapper">
              <span class="cart-notification__heading">You added ${event.detail.quantityAdded} ${event.detail.productAdded} to your cart</span>
            </div>
            
            ${closeButtonHtml}
          </div>
        </div>
      `;
      } else {
        this.classList.add("cart-notification--error");
        this.innerHTML = `
        <div class="cart-notification__overflow">
            <div class="cart-notification__wrapper">
              <div class="cart-notification__text-wrapper">
                <span class="cart-notification__heading">${event.detail.error}</span>
              </div>
              
              ${closeButtonHtml}
            </div>
          </div>
        </div>
      `;
      }

      this.clientHeight;
      this.hidden = false;
    }
  };

  window.customElements.define("cart-notification", CartNotification);










  /**
   * =============================================================
   * ## CART DRAWER
   * =============================================================
   */

  var CartDrawer = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();
      this.nextReplacementDelay = 0;
      this.rootDelegate.on("cart:refresh", this._rerenderCart.bind(this));
      this.addEventListener("variant:added", () => this.nextReplacementDelay = 600);
    }

    async _rerenderCart(event) {
      var _a;
      let cartContent = null, html = "";

      if (event.detail && event.detail["cart"]) {
        cartContent = event.detail["cart"];
        html = event.detail["cart"]["sections"]["mini-cart"];
      } else {
        const response = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${this.getAttribute("section")}`);
        html = await response.text();
      }

      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = html;

      setTimeout(async () => {
        var _a2;
        const previousPosition = this.querySelector(".drawer__content").scrollTop;

        this.innerHTML = fakeDiv.querySelector("cart-drawer").innerHTML;

        if (cartContent && cartContent["item_count"] === 0 && supportsAnimationsAPI) {
          this.querySelector(".drawer__content").animate({ opacity: [0, 1], transform: ["translateY(40px)", "translateY(0)"] }, { duration: 450, easing: "cubic-bezier(0.33, 1, 0.68, 1)" });
        } else {
          this.querySelector(".drawer__content").scrollTop = previousPosition;
        }

        if ((_a2 = event == null ? void 0 : event.detail) == null ? void 0 : _a2.openMiniCart) {
          this.clientWidth;
          this.open = true;
        }
      }, ((_a = event == null ? void 0 : event.detail) == null ? void 0 : _a.replacementDelay) || this.nextReplacementDelay);
      this.nextReplacementDelay = 0;
    }

    async attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      switch (name) {
        case "open":
          if (this.open) {
            this.querySelector(".drawer__content").scrollTop = 0;
          }
      }
    }
  };

  window.customElements.define("cart-drawer", CartDrawer);











  /**
   * =============================================================
   * ## SEARCH PAGE
   * =============================================================
   */
  var SearchPage = class extends HTMLElement {
    connectedCallback() {
      this.facetToolbar = document.getElementById("mobile-facet-toolbar");
      this.tabsNav = document.getElementById("search-tabs-nav");

      this.tabsNav.addEventListener("tabs-nav:changed", this._onCategoryChanged.bind(this));

      this._completeSearch();
    }

    get terms() {
      return this.getAttribute("terms");
    }

    get completeFor() {
      return JSON.parse(this.getAttribute("complete-for")).filter((item) => !(item === ""));
    }

    async _completeSearch() {

      const promisesList = [];

      this.completeFor.forEach((item) => {
        promisesList.push(fetch(`${window.themeVariables.routes.searchUrl}?section_id=${this.getAttribute("section-id")}&q=${this.terms}&type=${item}&options[prefix]=last&options[unavailable_products]=${window.themeVariables.settings.searchUnavailableProducts}`));
      });

      const responses = await Promise.all(promisesList);

      await Promise.all(responses.map(async (response) => {
        const div = document.createElement("div");
        div.innerHTML = await response.text();

        const categoryResultDiv = div.querySelector(".main-search__category-result");
        const tabNavItem = div.querySelector("#search-tabs-nav .tabs-nav__item");

        if (categoryResultDiv) {
          categoryResultDiv.setAttribute("hidden", "");
          this.insertAdjacentElement("beforeend", categoryResultDiv);
          this.tabsNav.addButton(tabNavItem);
        }
      }));
    }

    _onCategoryChanged(event) {
      const button = event.detail.button;

      this.facetToolbar.classList.toggle("is-collapsed", button.getAttribute("data-type") !== "product");
    }
  };

  window.customElements.define("search-page", SearchPage);











  /**
   * =============================================================
   * ## PRODUCT LIST
   * =============================================================
   */

  var ProductList = class extends CustomHTMLElement {
    constructor() {
      super();
      this.productListInner = this.querySelector(".product-list__inner");
      this.productItems = Array.from(this.querySelectorAll("product-item"));
    }

    connectedCallback() {
      this.addEventListener("prev-next:prev", this.previous.bind(this));
      this.addEventListener("prev-next:next", this.next.bind(this));
    }

    previous(event) {
      const columnGap = parseInt(getComputedStyle(this).getPropertyValue("--product-list-column-gap"));

      event.target.nextElementSibling.removeAttribute("disabled");

      event.target.toggleAttribute("disabled", this.productListInner.scrollLeft - (this.productListInner.clientWidth + columnGap) <= 0);

      this.productListInner.scrollBy({ left: -(this.productListInner.clientWidth + columnGap), behavior: "smooth" });
    }

    next(event) {
      const columnGap = parseInt(getComputedStyle(this).getPropertyValue("--product-list-column-gap"));

      event.target.previousElementSibling.removeAttribute("disabled");

      event.target.toggleAttribute("disabled", this.productListInner.scrollLeft + (this.productListInner.clientWidth + columnGap) * 2 >= this.productListInner.scrollWidth);

      this.productListInner.scrollBy({ left: (this.productListInner.clientWidth + columnGap), behavior: "smooth" });
    }

  };
  // __publicField(ProductList, "observedAttributes", ["hidden"]);
  
  window.customElements.define("product-list", ProductList);











  /**
   * =============================================================
   * ## ARTICLE ITEM
   * =============================================================
   */

  var ArticleItem = class extends HTMLElement {
    connectedCallback() {
      this.carouselList = this.querySelector(".article-item__carousel-list");
    }

  };
  
  window.customElements.define("article-item", ArticleItem);














  /**
   * =============================================================
   * ## Hero Image
   * =============================================================
   */

  var HeroImage = class extends CustomHTMLElement {
    connectedCallback() {
    }
  };

  window.customElements.define("hero-image", HeroImage);











  /**
   * =============================================================
   * ## BASE MEDIA
   * =============================================================
   */

  var BaseMedia = class extends HTMLElement {
    static get observedAttributes() {
      return ["playing"];
    }

    connectedCallback() {
      this._abortController = new AbortController();
      if (this.hasAttribute("autoplay")) {
        Motion.inView(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get playing() {
      return this.hasAttribute("playing");
    }

    get player() {
      return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
        get: (target, prop) => {
          return async () => {
            target = await target;
            this._playerHandler(target, prop);
          };
        }
      });
    }

    play() {
      if (!this.playing) {
        this.player.play();
      }
    }

    pause() {
      if (this.playing) {
        this.player.pause();
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "playing") {
        if (oldValue === null && newValue === "") {
          this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));
          if (this.hasAttribute("group")) {
            Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
              itemToPause.pause();
            });
          }
        } else if (newValue === null) {
          this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
        }
      }
    }
  };










  /**
   * =============================================================
   * ## VIDEO MEDIA
   * =============================================================
   */

  var onYouTubePromise = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = () => resolve();
  });

  var VideoMedia = class extends BaseMedia {
    connectedCallback() {
      super.connectedCallback();

      if (!this.hasAttribute("autoplay")) {
        this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
      }

      if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
        this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment(`
          <slot></slot>
          
          <svg part="play-button" fill="none" width="48" height="48" viewBox="0 0 48 48">
            <path d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24s10.745 24 24 24 24-10.745 24-24Z" fill="#B1967E"/>
            <path d="M18.578 32.629a.375.375 0 0 1-.578-.316V15.687c0-.297.328-.476.578-.316l12.931 8.314c.23.147.23.483 0 .63L18.578 32.63Z" fill="#000"/>
            <path d="M24 .5C36.979.5 47.5 11.021 47.5 24S36.979 47.5 24 47.5.5 36.979.5 24 11.021.5 24 .5Z" stroke="#000" stroke-opacity="0"/>
          </svg>
        `));
      }
    }

    _playerTarget() {
      if (this.hasAttribute("host")) {
        this.setAttribute("loaded", "");
        return new Promise(async (resolve) => {
          const templateElement = this.querySelector("template");

          if (templateElement) {
            templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
          }

          const muteVideo = this.hasAttribute("autoplay") || window.matchMedia("screen and (max-width: 1024px)").matches;

          const script = document.createElement("script");
          script.type = "text/javascript";

          if (this.getAttribute("host") === "youtube") {
            if (!window.YT || !window.YT.Player) {
              script.src = "https://www.youtube.com/iframe_api";
              document.head.appendChild(script);
              await new Promise((resolve2) => {
                script.onload = resolve2;
              });
            }

            await onYouTubePromise;

            this.setAttribute("can-play", "");

            const player = new YT.Player(this.querySelector("iframe"), {
              events: {
                "onReady": () => {
                  if (muteVideo) {
                    player.mute();
                  }
                  resolve(player);
                },
                "onStateChange": (event) => {
                  if (event.data === YT.PlayerState.PLAYING) {
                    this.setAttribute("playing", "");
                  } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                    this.removeAttribute("playing");
                  }
                }
              }
            });
          }

          if (this.getAttribute("host") === "vimeo") {
            if (!window.Vimeo || !window.Vimeo.Player) {
              script.src = "https://player.vimeo.com/api/player.js";
              document.head.appendChild(script);
              await new Promise((resolve2) => {
                script.onload = resolve2;
              });
            }

            const player = new Vimeo.Player(this.querySelector("iframe"));

            if (muteVideo) {
              player.setMuted(true);
            }

            this.setAttribute("can-play", "");

            player.on("play", () => {
              this.setAttribute("playing", "");
            });

            player.on("pause", () => this.removeAttribute("playing"));

            player.on("ended", () => this.removeAttribute("playing"));

            resolve(player);
          }
        });
      } else {
        const videoElement = this.querySelector("video");
        this.setAttribute("loaded", "");
        this.setAttribute("can-play", "");

        videoElement.addEventListener("play", () => {
          this.setAttribute("playing", "");
        });

        videoElement.addEventListener("pause", () => {
          if (!videoElement.seeking && videoElement.paused) {
            this.removeAttribute("playing");
          }
        });

        return videoElement;
      }
    }

    _playerHandler(target, prop) {
      if (this.getAttribute("host") === "youtube") {
        prop === "play" ? target.playVideo() : target.pauseVideo();
      } else {
        target[prop]();
      }
    }
  };

  if (!window.customElements.get("video-media")) {
    window.customElements.define("video-media", VideoMedia);
  }









  /**
   * =============================================================
   * ## Map Element
   * =============================================================
   */

  var MapElement = class extends HTMLElement {
    connectedCallback() {
      this.options = JSON.parse(this.getAttribute('data-map-settings'));
      this.customMarker = this.getAttribute('data-marker');

      if (this.options['apiKey'] && this.options['mapAddress']) {
        this._loadScript().then(this._initMap.bind(this));
      }
    }

    _loadScript() {
      let _this1 = this;
      return new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        document.body.appendChild(script);
        script.onload = resolve;
        script.onerror = reject;
        script.async = true;
        script.src = 'https://maps.googleapis.com/maps/api/js?key=' + _this1.options['apiKey'];
      });
    }

    _initMap() {
      let _this2 = this;

      var geocoder = new google.maps.Geocoder();

      geocoder.geocode({ address: this.options['mapAddress'] }, function (results, status) {
        if (status !== google.maps.GeocoderStatus.OK) {
          if (Shopify.designMode) {}
        } else {
          var mapOptions = {
            zoom: _this2.options['zoom'],
            center: results[0].geometry.location,
            draggable: true,
            clickableIcons: false,
            scrollwheel: true,
            disableDoubleClickZoom: true,
            disableDefaultUI: false
          };

          var map = new google.maps.Map(_this2.querySelector('.featured-map__gmap'), mapOptions),
              center = map.getCenter();

          map.setCenter(center);

          var icon = {
            path: "M32.7374478,5.617 C29.1154478,1.995 24.2994478,0 19.1774478,0 C14.0544478,0 9.23944778,1.995 5.61744778,5.617 C-1.08555222,12.319 -1.91855222,24.929 3.81344778,32.569 L19.1774478,54.757 L34.5184478,32.6 C40.2734478,24.929 39.4404478,12.319 32.7374478,5.617 Z M19.3544478,26 C15.4954478,26 12.3544478,22.859 12.3544478,19 C12.3544478,15.141 15.4954478,12 19.3544478,12 C23.2134478,12 26.3544478,15.141 26.3544478,19 C26.3544478,22.859 23.2134478,26 19.3544478,26 Z",
            fillColor: _this2.options['markerColor'],
            fillOpacity: 1,
            anchor: new google.maps.Point(15, 55),
            strokeWeight: 0,
            scale: 0.6
          };

          new google.maps.Marker({
            map: map,
            position: map.getCenter(),
            icon: _this2.customMarker
          });

          var styledMapType = new google.maps.StyledMapType(JSON.parse(_this2.querySelector('[data-gmap-style]').innerHTML));

          map.mapTypes.set('styled_map', styledMapType);
          map.setMapTypeId('styled_map');

          google.maps.event.addDomListener(window, 'resize', function () {
            google.maps.event.trigger(map, 'resize');
            map.setCenter(center);
          });
        }
      });
    }
  }

  window.customElements.define("map-element", MapElement);











  /**
   * =============================================================
   * ## Animate On Scroll
   * =============================================================
   */

  var AnimateOnScroll = function() {
    Motion.inView("[data-animate]", (element) => {
      element.target.classList.add("animated");
    }, {
      amount: 0.3
    });
  };








  /**
   * =============================================================
   * ## Page Transitions
   * =============================================================
   */

  var PageTransitions = function() {
  
    var documentDelegate = new Delegate(document.body);

    window.onpageshow = function(evt) {
      // Removes unload class when returning to page via history
      if (evt.persisted) {
        document.body.classList.remove('unloading');
      }
    };

    // Add disable transition class to various link types
    // `/s/files` are for files saved on Shopify
    documentDelegate.on('click', 'a[href]:not([href^="#"]):not([href^="javascript:"]):not([href^="mailto:"]):not([href^="tel:"]):not([target="_blank"]):not([data-no-instant]):not([href*="/s/files"])', onPageUnload)

    function onPageUnload(evt, target) {

      if (evt.metaKey) {
        return;
      }

      /**
       * We have a click event listener in `header-main` which is being overridden
       * by the page transitions. This ensures that on touch devices, users can
       * still open a dropdown menu in the header using touch rather than be directed
       * to the new page.
       */
      if (evt.target.classList.contains("header__linklist-link") && evt.target.getAttribute("aria-expanded") && !window.matchMedia("(hover: hover)").matches) {
        if (!window.themeVariables.settings.showPageTransitions) {}
        // return;
      }

      evt.preventDefault();
      evt.stopPropagation();

      document.body.classList.add('unloading');

      var src = target.getAttribute('href');
      
      window.setTimeout(function() {
        location.href = src;
      }, 50);

    }
  };






















  /**
   * =============================================================
   * ## DOM READY
   * =============================================================
   */

  (() => {
    new InputBindingManager();
  })();

  if (window.themeVariables.settings.showPageTransitions) {
    PageTransitions();
  }

  AnimateOnScroll();

  // Run AnimateOnScroll in Shopify Theme Editor
  if (Shopify.designMode) {
    document.addEventListener("shopify:section:select", (event) => {
      AnimateOnScroll();
    });

    document.addEventListener("shopify:section:load", (event) => {
      AnimateOnScroll();
    });
  }

  // Get window height and store as CSS variable
  (() => {
    let previousClientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth;

    let setViewportProperty = () => {
      const clientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth, clientHeight = window.visualViewport ? window.visualViewport.height : document.documentElement.clientHeight;

      if (clientWidth === previousClientWidth) {
        return;
      }

      requestAnimationFrame(() => {
        document.documentElement.style.setProperty("--window-height", clientHeight + "px");
        previousClientWidth = clientWidth;
      });
    };

    setViewportProperty();

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setViewportProperty);
    } else {
      window.addEventListener("resize", setViewportProperty);
    }
  })();

  (() => {
    /**
     * Automatic Discounts Trade Login
     * If a user adds items to cart and then logs in as a trade customer, they won't see the automatic discounts. This also breaks any cart item updates such as removing or quantity changes. We need to determine when a customer has logged in successfully, fetch the updated cart from Shopify, and then update the UI.
     * Solution inspired by:
     * https://community.shopify.com/c/shopify-scripts/add-custom-script-when-login-successfully/m-p/1718776/highlight/true#M3643
     */
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const loginSuccess = urlParams.get('login_success');
    
    if (loginSuccess) {
      // Fetches the updated cart including automatic discounts
      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true
        // Allows to make sure the request is fired even when submitting the form
      }).then(function(response) {
        if (response.ok) {
          // Refreshes the cart UI including cart drawer
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
        }
      });
      // Remove params from URL to avoid multiple events to fire. 
      window.history.pushState({}, null, window.location.href.split('?')[0]);
    }
  })()

  // Smooth scrolling for internal links
  (() => {
    var documentDelegate = new Delegate(document.body);

    documentDelegate.on('click', '[href^="#"], [data-href]', function (event, target) {
      var selector = target.hasAttribute('href') ? target.getAttribute('href') : target.getAttribute('data-href');

      if (selector === '#') {
        return;
      }

      var element = document.querySelector(selector);
      var offset = parseInt(target.getAttribute('data-offset') || 0);
      var shouldAnimate = !MediaFeatures.prefersReducedMotion();

      window.scrollTo({ 
        behavior: shouldAnimate ? "smooth" : "auto", 
        top: element.offsetTop - offset 
      });

      event.preventDefault();
    });
  })();


  // Open external links in a new tab: https://ed.codes/blog/shopify-open-external-links-in-new-tab
  (function () {
    var links = document.links;
    for (let i = 0, linksLength = links.length ; i < linksLength ; i++) {
      if (links[i].hostname !== window.location.hostname) {
        links[i].target = '_blank';
        links[i].rel = 'noreferrer noopener';
      }
    }
  })();

  // RTE utilities
  (() => {
    document.querySelectorAll(".rte table").forEach((table) => {
      table.outerHTML = '<div class="table-wrapper">' + table.outerHTML + "</div>";
    });

    document.querySelectorAll(".rte iframe").forEach((iframe) => {
      if (iframe.src.indexOf("youtube") !== -1 || iframe.src.indexOf("youtu.be") !== -1 || iframe.src.indexOf("vimeo") !== -1) {
        iframe.outerHTML = '<div class="video-wrapper">' + iframe.outerHTML + "</div>";
      }
    });
  })();

  // Tabbing focus
  (() => {
    document.addEventListener("keyup", function(event) {
      if (event.key === "Tab") {
        document.body.classList.remove("no-focus-outline");
        document.body.classList.add("focus-outline");
      }
    });
  })();

})();